import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Boxes,
  Eye,
  FileSpreadsheet,
  PackageCheck,
  RotateCcw,
  Send
} from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';
import InventoryDevicePicker from '../components/InventoryDevicePicker';

const INVENTORY_DASHBOARD_VIEW = 'inventory-dashboard';
const INVENTORY_MODULE_VIEW = 'inventory-module';
const LIVE_INVENTORY_REPORT_VIEW = 'live-inventory-report';
const INVENTORY_MODULE_SUBMENU_ACTIONS = {
  'inventory-download-report': { type: 'view', mode: 'stock', status: 'all', pageKey: 'stock' },
  'inventory-return-report': { type: 'view', mode: 'returns', status: 'all', pageKey: 'returns' },
  'inventory-stock-total': { type: 'view', mode: 'stock', status: 'all', pageKey: 'stock' },
  'inventory-stock-available': { type: 'view', mode: 'stock', status: 'available', pageKey: 'available' },
  'inventory-stock-sold': { type: 'view', mode: 'stock', status: 'sold', pageKey: 'sold' },
  'inventory-stock-defective': { type: 'view', mode: 'stock', status: 'defective', pageKey: 'defective' },
  'inventory-transfer-stock': { type: 'transfer' },
  'inventory-transfer-history': { type: 'view', mode: 'transfers', status: 'all', pageKey: 'transfers' },
  'inventory-product-type': { type: 'form', form: 'type', pageKey: 'type' },
  'inventory-oem-name': { type: 'form', form: 'oem', pageKey: 'oem' },
  'inventory-device-name': { type: 'form', form: 'deviceName', pageKey: 'deviceName' },
  'inventory-new-product': { type: 'form', form: 'product', pageKey: 'product' },
  'inventory-add-stock': { type: 'form', form: 'stock', pageKey: 'addStock' },
  'inventory-bulk-upload': { type: 'form', form: 'bulk', pageKey: 'bulkUpload' },
  'inventory-return-entry': { type: 'form', form: 'return', pageKey: 'returnEntry' }
};
const LIVE_INVENTORY_REPORT_SUBMENU_ACTIONS = {
  'live-stock-list': { type: 'view', mode: 'stock', status: 'all', pageKey: 'stock' },
  'live-purchase-inventory': { type: 'view', mode: 'purchase', status: 'all', pageKey: 'purchase' },
  'live-inventory-return-report': { type: 'view', mode: 'returns', status: 'all', pageKey: 'returns' },
  'live-available-stock': { type: 'view', mode: 'stock', status: 'available', pageKey: 'available' },
  'live-sold-devices': { type: 'view', mode: 'stock', status: 'sold', pageKey: 'sold' },
  'live-defective-stock': { type: 'view', mode: 'stock', status: 'defective', pageKey: 'defective' },
  'live-low-stock-alert': { type: 'view', mode: 'stock', status: 'all', pageKey: 'lowStock' },
  'live-office-location-stock': { type: 'view', mode: 'warehouses', status: 'all', pageKey: 'warehouse' },
  'live-assign-employee': { type: 'assign' },
  'live-stock-transfer': { type: 'transfer' },
  'live-transfer-history': { type: 'view', mode: 'transfers', status: 'all', pageKey: 'transfers' },
  'live-product-type': { type: 'form', form: 'type', pageKey: 'type' },
  'live-oem-name': { type: 'form', form: 'oem', pageKey: 'oem' },
  'live-device-name': { type: 'form', form: 'deviceName', pageKey: 'deviceName' },
  'live-product-master': { type: 'form', form: 'product', pageKey: 'product' },
  'live-add-stock': { type: 'form', form: 'stock', pageKey: 'addStock' },
  'live-bulk-upload': { type: 'form', form: 'bulk', pageKey: 'bulkUpload' },
  'live-return-entry': { type: 'form', form: 'return', pageKey: 'returnEntry' }
};
const LIVE_INVENTORY_REPORT_DEFAULT_ACTION = { type: 'view', mode: 'stock', status: 'all', pageKey: 'stock' };

export default function Inventory({ inventoryView = INVENTORY_DASHBOARD_VIEW }) {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const inventoryModuleSubmenuAction = INVENTORY_MODULE_SUBMENU_ACTIONS[inventoryView] || null;
  const liveInventoryReportSubmenuAction = LIVE_INVENTORY_REPORT_SUBMENU_ACTIONS[inventoryView] || null;
  const sidebarInventoryAction = inventoryModuleSubmenuAction
    || liveInventoryReportSubmenuAction
    || (inventoryView === LIVE_INVENTORY_REPORT_VIEW ? LIVE_INVENTORY_REPORT_DEFAULT_ACTION : null);
  const activeInventoryView = inventoryView === LIVE_INVENTORY_REPORT_VIEW || liveInventoryReportSubmenuAction
    ? LIVE_INVENTORY_REPORT_VIEW
    : inventoryView === INVENTORY_MODULE_VIEW || inventoryModuleSubmenuAction
      ? INVENTORY_MODULE_VIEW
      : INVENTORY_DASHBOARD_VIEW;
  const defaultInventoryPage = activeInventoryView === LIVE_INVENTORY_REPORT_VIEW
    ? 'home'
    : activeInventoryView === INVENTORY_MODULE_VIEW
      ? 'module'
      : 'dashboard';
  const [stock, setStock] = useState([]);
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [deviceOems, setDeviceOems] = useState([]);
  const [deviceNames, setDeviceNames] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [returns, setReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stockTransfers, setStockTransfers] = useState([]);
  const [warehouseAssignments, setWarehouseAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [showStockForm, setShowStockForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [showOemForm, setShowOemForm] = useState(false);
  const [showDeviceNameForm, setShowDeviceNameForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [, setLastRefreshed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [editStockId, setEditStockId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [transferSearchTerm, setTransferSearchTerm] = useState('');
  const [returnEntrySearch, setReturnEntrySearch] = useState('');
  const [transferStatusFilter, setTransferStatusFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [viewMode, setViewMode] = useState('stock');
  const [inventoryPage, setInventoryPage] = useState(defaultInventoryPage);
  const [returnInventoryId, setReturnInventoryId] = useState('');
  const [returnCustomerId, setReturnCustomerId] = useState('');
  const [returnSendToOemId, setReturnSendToOemId] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [returnCondition, setReturnCondition] = useState('good');
  const [editWarehouseId, setEditWarehouseId] = useState(null);
  const [warehouseName, setWarehouseName] = useState('');
  const [warehouseAddress, setWarehouseAddress] = useState('');
  const [warehouseNote, setWarehouseNote] = useState('');
  const [transferInventoryId, setTransferInventoryId] = useState('');
  const [transferToWarehouseId, setTransferToWarehouseId] = useState('');
  const [transferReceiverUserId, setTransferReceiverUserId] = useState('');
  const [transferNewWarehouseName, setTransferNewWarehouseName] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferMode, setTransferMode] = useState('bulk');
  const [bulkFromWarehouseId, setBulkFromWarehouseId] = useState('');
  const [bulkToWarehouseId, setBulkToWarehouseId] = useState('');
  const [bulkReceiverUserId, setBulkReceiverUserId] = useState('');
  const [bulkTransferNote, setBulkTransferNote] = useState('');
  const [bulkTransferSearch, setBulkTransferSearch] = useState('');
  const [bulkSelectedStockIds, setBulkSelectedStockIds] = useState([]);
  const [selectedAcceptTransferIds, setSelectedAcceptTransferIds] = useState([]);
  const [assignmentWarehouseId, setAssignmentWarehouseId] = useState('');
  const [assignmentEmployeeIds, setAssignmentEmployeeIds] = useState([]);
  const [productId, setProductId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceOemName, setDeviceOemName] = useState('');
  const [stockDeviceType, setStockDeviceType] = useState('GPS');
  const [imei, setImei] = useState('');
  const [simNo, setSimNo] = useState('');
  const [simNo2, setSimNo2] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [warehouse, setWarehouse] = useState('Main');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [stockStatus, setStockStatus] = useState('available');
  const [vendorName, setVendorName] = useState('');
  const [vendorMobile, setVendorMobile] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [paymentMode, setPaymentMode] = useState('credit');
  const [paidAmount, setPaidAmount] = useState('');
  const [purchaseNote, setPurchaseNote] = useState('');
  const [pName, setPName] = useState('');
  const [pType, setPType] = useState('');
  const [pModel, setPModel] = useState('');
  const [productTypeName, setProductTypeName] = useState('');
  const [newDeviceOemName, setNewDeviceOemName] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceNameOem, setNewDeviceNameOem] = useState('');
  const [newDeviceNameType, setNewDeviceNameType] = useState('GPS');

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });
  const canModifyInventory = ['superadmin', 'admin', 'whitelabel_admin'].includes(currentUser?.role);
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const canTransferStock = canModifyInventory || currentUser?.role === 'employee';
  const canViewPurchaseDetails = canModifyInventory;
  const canManageMaster = (item) => canModifyInventory && (currentUser?.role === 'superadmin' || Number(item.admin_id) === Number(currentUser?.id));
  const normalizeMasterValue = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const hasDuplicateMaster = (items, value, field = 'name', excludeId = null, scopeAdminId = null) => {
    const key = normalizeMasterValue(value);
    if (!key) return false;
    const scopedAdminId = scopeAdminId === null ? null : Number(scopeAdminId || 0);
    return (items || []).some((item) => (
      (!excludeId || String(item.id) !== String(excludeId))
      && (
        currentUser?.role !== 'superadmin'
        || (scopedAdminId === null
          ? Number(item.admin_id || 0) === 0
          : [0, scopedAdminId].includes(Number(item.admin_id || 0)))
      )
      && normalizeMasterValue(item[field]) === key
    ));
  };

  const fetchAll = async () => {
    try {
      const [stockRes, prodRes, typeRes, oemRes, deviceNameRes, vendorRes, lowStockRes, returnRes, customerRes, warehouseRes, transferRes, assignmentRes, userRes] = await Promise.all([
        axios.get(`${API_URL}/api/inventory/stock`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/products`, { headers: getHeaders() }),
        canModifyInventory ? axios.get(`${API_URL}/api/inventory/product-types`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
        canModifyInventory ? axios.get(`${API_URL}/api/inventory/device-oems`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
        canModifyInventory ? axios.get(`${API_URL}/api/inventory/device-names`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
        canViewPurchaseDetails ? axios.get(`${API_URL}/api/purchases/vendors`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
        canViewPurchaseDetails ? axios.get(`${API_URL}/api/inventory/lowstock`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
        canViewPurchaseDetails ? axios.get(`${API_URL}/api/returns`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
        axios.get(`${API_URL}/api/customers`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/warehouses`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/stock-transfers`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/inventory/warehouse-assignments`, { headers: getHeaders() }),
        canModifyInventory ? axios.get(`${API_URL}/api/users`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
      ]);
      setStock(stockRes.data);
      setProducts(prodRes.data);
      setProductTypes(typeRes.data);
      setDeviceOems(oemRes.data);
      setDeviceNames(deviceNameRes.data);
      setVendors(vendorRes.data || []);
      setLowStock(lowStockRes.data || []);
      setReturns(returnRes.data || []);
      setCustomers(customerRes.data || []);
      setWarehouses(warehouseRes.data || []);
      setStockTransfers(transferRes.data || []);
      setWarehouseAssignments(assignmentRes.data || []);
      setUsers(Array.isArray(userRes.data) ? userRes.data : (userRes.data?.users || []));
      setLastRefreshed(new Date());
      if (!pType && typeRes.data.length > 0) setPType(typeRes.data[0].name);
      if (!stockDeviceType && typeRes.data.length > 0) setStockDeviceType(typeRes.data[0].name);
      if (!newDeviceNameType && typeRes.data.length > 0) setNewDeviceNameType(typeRes.data[0].name);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchAll();
    const refreshTimer = setInterval(fetchAll, 60000);
    return () => clearInterval(refreshTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddProductType = async (e) => {
    e.preventDefault();
    if (hasDuplicateMaster(productTypes, productTypeName)) return alert('Product type already exists');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/product-types`,
        { name: productTypeName.trim() },
        { headers: getHeaders() });
      alert('Product type added!');
      setPType(res.data.name || productTypeName);
      setProductTypeName('');
      setShowTypeForm(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeviceOemName = async (e) => {
    e.preventDefault();
    if (hasDuplicateMaster(deviceOems, newDeviceOemName)) return alert('Device OEM name already exists');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/device-oems`,
        { name: newDeviceOemName.trim() },
        { headers: getHeaders() });
      alert('Device OEM name added!');
      setDeviceOemName(res.data.name || newDeviceOemName);
      setNewDeviceNameOem(res.data.name || newDeviceOemName);
      setNewDeviceOemName('');
      setShowOemForm(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeviceName = async (e) => {
    e.preventDefault();
    if (hasDuplicateMaster(deviceNames, newDeviceName)) return alert('Device name already exists');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/device-names`,
        { name: newDeviceName.trim(), device_oem_name: newDeviceNameOem.trim(), device_type: newDeviceNameType.trim() },
        { headers: getHeaders() });
      alert('Device name added!');
      setDeviceName(res.data.name || newDeviceName);
      setDeviceOemName(newDeviceNameOem);
      setStockDeviceType(newDeviceNameType || 'GPS');
      setNewDeviceName('');
      setShowDeviceNameForm(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (hasDuplicateMaster(products, pName)) return alert('Product already exists');
    if (pModel.trim() && hasDuplicateMaster(products, pModel, 'model')) return alert('Product model already exists');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/inventory/products`,
        { name: pName.trim(), type: pType.trim(), model: pModel.trim() },
        { headers: getHeaders() });
      alert('Product added!');
      setShowProductForm(false);
      setPName(''); setPModel('');
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetStockForm = () => {
    setEditStockId(null);
    setProductId('');
    setDeviceName('');
    setDeviceOemName('');
    setStockDeviceType('GPS');
    setImei('');
    setSimNo('');
    setSimNo2('');
    setSerialNo('');
    setWarehouse(warehouses[0]?.name || 'Main');
    setPurchasePrice('');
    setPurchaseDate('');
    setStockStatus('available');
    setVendorName('');
    setVendorMobile('');
    setInvoiceNo('');
    setPaymentMode('credit');
    setPaidAmount('');
    setPurchaseNote('');
  };

  const formatDateInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toISOString().slice(0, 10);
  };

  const startEditStock = (item) => {
    if (!canModifyInventory) return alert('Only admin can modify stock records');
    setEditStockId(item.id);
    setProductId(item.product_id || '');
    setDeviceName(item.device_name || item.name || '');
    setDeviceOemName(item.device_oem_name || '');
    setStockDeviceType(item.device_type || item.type || 'GPS');
    setImei(item.imei || '');
    setSimNo(item.sim_no || '');
    setSimNo2(item.sim_no_2 || '');
    setSerialNo(item.serial_no || '');
    setWarehouse(item.warehouse || 'Main');
    setPurchasePrice(item.purchase_price || '');
    setPurchaseDate(formatDateInput(item.purchase_date));
    setStockStatus(item.status || 'available');
    setVendorName(item.vendor_name || '');
    setVendorMobile(item.vendor_mobile || '');
    setInvoiceNo(item.invoice_no || '');
    setPaymentMode(item.payment_mode || 'credit');
    setPaidAmount(item.paid_amount || '');
    setPurchaseNote(item.purchase_note || '');
    setShowStockForm(true);
    setShowTypeForm(false);
    setShowProductForm(false);
    setShowOemForm(false);
    setShowDeviceNameForm(false);
    setShowBulkForm(false);
    setShowWarehouseForm(false);
    setShowTransferForm(false);
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        product_id: productId || null,
        device_name: deviceName,
        device_oem_name: deviceOemName,
        device_type: stockDeviceType,
        imei,
        sim_no_1: simNo,
        sim_no_2: simNo2,
        serial_no: serialNo,
        warehouse,
        status: stockStatus,
        ...(canViewPurchaseDetails ? {
          purchase_price: purchasePrice,
          purchase_date: purchaseDate,
          vendor_name: vendorName,
          vendor_mobile: vendorMobile,
          invoice_no: invoiceNo,
          payment_mode: paymentMode,
          paid_amount: paidAmount,
          purchase_note: purchaseNote
        } : {})
      };
      if (editStockId) {
        if (!canModifyInventory) return alert('Only admin can update stock records');
        await axios.put(`${API_URL}/api/inventory/stock/${editStockId}`, payload, { headers: getHeaders() });
        alert('Stock updated!');
      } else {
        await axios.post(`${API_URL}/api/inventory/stock`, payload, { headers: getHeaders() });
        alert('Stock added!');
      }
      setShowStockForm(false);
      resetStockForm();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const deleteStock = async (item) => {
    if (!isSuperAdmin) return alert('Only Super Admin can delete stock records');
    const confirmation = window.prompt(`Delete stock ${item.imei || item.serial_no || item.device_name || item.name} permanently?\nType DELETE to confirm.`);
    if (confirmation !== 'DELETE') return;
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/inventory/stock/${item.id}?force=1`, { headers: getHeaders() });
      alert('Stock deleted!');
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const cleanStockDetails = async (item) => {
    if (!isSuperAdmin) return alert('Only Super Admin can clean stock records');
    if (!window.confirm(`Clean stock details for ${item.imei || item.serial_no || item.device_name || item.name}? This will clear IMEI, SIM, serial, purchase, transfer, and linked sale references.`)) return;
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/api/inventory/stock/${item.id}/clean-details`, {}, { headers: getHeaders() });
      alert('Stock details cleaned!');
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Clean failed');
    } finally {
      setLoading(false);
    }
  };

  const updateProductType = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can modify master records');
    const name = window.prompt('Product type name', item.name);
    if (!name || !name.trim()) return;
    if (hasDuplicateMaster(productTypes, name, 'name', item.id, item.admin_id)) return alert('Product type already exists');
    try {
      await axios.put(`${API_URL}/api/inventory/product-types/${item.id}`, { name: name.trim() }, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const deleteProductType = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can delete own master records');
    const confirmation = window.prompt(`Delete product type ${item.name}?\nType DELETE to confirm.`);
    if (confirmation !== 'DELETE') return;
    try {
      await axios.delete(`${API_URL}/api/inventory/product-types/${item.id}`, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const updateDeviceOem = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can modify master records');
    const name = window.prompt('Device OEM name', item.name);
    if (!name || !name.trim()) return;
    if (hasDuplicateMaster(deviceOems, name, 'name', item.id, item.admin_id)) return alert('Device OEM name already exists');
    try {
      await axios.put(`${API_URL}/api/inventory/device-oems/${item.id}`, { name: name.trim() }, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const deleteDeviceOem = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can delete own master records');
    const confirmation = window.prompt(`Delete OEM ${item.name}?\nType DELETE to confirm.`);
    if (confirmation !== 'DELETE') return;
    try {
      await axios.delete(`${API_URL}/api/inventory/device-oems/${item.id}`, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const updateDeviceMaster = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can modify master records');
    const name = window.prompt('Device name', item.name);
    if (!name || !name.trim()) return;
    if (hasDuplicateMaster(deviceNames, name, 'name', item.id, item.admin_id)) return alert('Device name already exists');
    const oem = window.prompt('Device OEM name', item.device_oem_name || '') ?? '';
    const type = window.prompt('Device type', item.device_type || 'GPS') || 'GPS';
    try {
      await axios.put(`${API_URL}/api/inventory/device-names/${item.id}`, {
        name: name.trim(),
        device_oem_name: oem.trim(),
        device_type: type.trim()
      }, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const deleteDeviceMaster = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can delete own master records');
    const confirmation = window.prompt(`Delete device name ${item.name}?\nType DELETE to confirm.`);
    if (confirmation !== 'DELETE') return;
    try {
      await axios.delete(`${API_URL}/api/inventory/device-names/${item.id}`, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const updateProductMaster = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can modify master records');
    const name = window.prompt('Product name', item.name);
    if (!name || !name.trim()) return;
    const type = window.prompt('Product type', item.type || 'GPS') || 'GPS';
    const model = window.prompt('Model', item.model || '') ?? '';
    if (hasDuplicateMaster(products, name, 'name', item.id, item.admin_id)) return alert('Product already exists');
    if (model.trim() && hasDuplicateMaster(products, model, 'model', item.id, item.admin_id)) return alert('Product model already exists');
    try {
      await axios.put(`${API_URL}/api/inventory/products/${item.id}`, {
        name: name.trim(),
        type: type.trim(),
        model: model.trim(),
        description: item.description || ''
      }, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const deleteProductMaster = async (item) => {
    if (!canManageMaster(item)) return alert('Only admin can delete own master records');
    const confirmation = window.prompt(`Delete product ${item.name}?\nType DELETE to confirm.`);
    if (confirmation !== 'DELETE') return;
    try {
      await axios.delete(`${API_URL}/api/inventory/products/${item.id}`, { headers: getHeaders() });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) return alert('Please select a file');
    setLoading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await axios.post(`${API_URL}/api/bulk/stock`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setBulkResult(res.data);
      fetchAll();
    } catch (err) {
      const response = err.response?.data;
      if (Array.isArray(response?.errors)) {
        setBulkResult({
          message: response.message || 'Bulk stock upload rejected. Fix listed errors and upload again.',
          total: response.total || 0,
          inserted: response.inserted || 0,
          errors: response.errors
        });
        alert(response.message || 'Bulk stock upload rejected');
      } else {
        alert(response?.message || 'Upload failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadLocalStockSample = () => {
    const rows = canViewPurchaseDetails
      ? [
        ['Device Name', 'Device Oem Name', 'Device Type', 'IMEI Number', 'SIM Number 1', 'SIM Number 2', 'Serial Number', 'Office Location', 'Purchase Price', 'Purchase Date', 'Status', 'Vendor Name', 'Vendor Mobile', 'Invoice Number', 'Payment Mode', 'Paid Amount', 'Purchase Note'],
        ['GT06N Tracker', 'Concox', 'GPS', '123456789012345', '899110120000000001', '899110120000000101', 'GT06N-001', 'Main', 850, '05/07/2026', 'available', 'ABC Devices', '9876543210', 'INV-001', 'credit', 0, 'Opening stock'],
        ['FMB920 Tracker', 'Teltonika', 'AIS140', '123456789012346', '899110120000000002', '899110120000000102', 'FMB920-001', 'Main', 1200, '05/07/2026', 'available', 'GPS World', '9876500000', 'INV-002', 'upi', 1200, ''],
      ]
      : [
        ['Device Name', 'Device Oem Name', 'Device Type', 'IMEI Number', 'SIM Number 1', 'SIM Number 2', 'Serial Number', 'Office Location', 'Status'],
        ['GT06N Tracker', 'Concox', 'GPS', '123456789012345', '899110120000000001', '899110120000000101', 'GT06N-001', 'Main', 'available'],
        ['FMB920 Tracker', 'Teltonika', 'AIS140', '123456789012346', '899110120000000002', '899110120000000102', 'FMB920-001', 'Main', 'available'],
      ];
    const csv = rows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_stock.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    if (!returnInventoryId) return alert('Please select inventory item');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/returns`, {
        inventory_id: returnInventoryId,
        customer_id: returnCustomerId || null,
        received_from_customer_id: returnCustomerId || null,
        send_to_oem_id: returnSendToOemId || null,
        send_to_oem_name: deviceOems.find((oem) => String(oem.id) === String(returnSendToOemId))?.name || '',
        reason: returnReason,
        condition_status: returnCondition
      }, { headers: getHeaders() });
      alert('Inventory return recorded!');
      setShowReturnForm(false);
      setReturnInventoryId('');
      setReturnEntrySearch('');
      setReturnCustomerId('');
      setReturnSendToOemId('');
      setReturnReason('');
      setReturnCondition('good');
      setViewMode('returns');
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Return entry failed');
    } finally {
      setLoading(false);
    }
  };

  const resetWarehouseForm = () => {
    setEditWarehouseId(null);
    setWarehouseName('');
    setWarehouseAddress('');
    setWarehouseNote('');
  };

  const resetTransferForm = () => {
    setTransferInventoryId('');
    setTransferToWarehouseId('');
    setTransferReceiverUserId('');
    setTransferNewWarehouseName('');
    setTransferNote('');
    setTransferMode('bulk');
    setBulkFromWarehouseId('');
    setBulkToWarehouseId('');
    setBulkReceiverUserId('');
    setBulkTransferNote('');
    setBulkTransferSearch('');
    setBulkSelectedStockIds([]);
    setSelectedAcceptTransferIds([]);
  };

  const handleWarehouseSubmit = async (e) => {
    e.preventDefault();
    if (!warehouseName.trim()) return alert('Please enter an office location name');
    setLoading(true);
    try {
      const payload = {
        name: warehouseName.trim(),
        address: warehouseAddress,
        note: warehouseNote
      };
      if (editWarehouseId) {
        await axios.put(`${API_URL}/api/inventory/warehouses/${editWarehouseId}`, payload, { headers: getHeaders() });
        alert('Office Location updated!');
      } else {
        await axios.post(`${API_URL}/api/inventory/warehouses`, payload, { headers: getHeaders() });
        alert('Office Location created!');
      }
      resetWarehouseForm();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Office Location save failed');
    } finally {
      setLoading(false);
    }
  };

  const startEditWarehouse = (item) => {
    if (!canModifyInventory) return alert('Only admin can modify office location');
    setEditWarehouseId(item.id);
    setWarehouseName(item.name || '');
    setWarehouseAddress(item.address || '');
    setWarehouseNote(item.note || '');
    setInventoryPage('warehouse');
    setShowWarehouseForm(true);
    setShowTransferForm(false);
    setShowStockForm(false);
  };

  const deleteWarehouse = async (item) => {
    if (!isSuperAdmin) return alert('Only Super Admin can delete office location');
    const confirmation = window.prompt(`Delete office location ${item.name}?\nType DELETE to confirm.`);
    if (confirmation !== 'DELETE') return;
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/inventory/warehouses/${item.id}`, { headers: getHeaders() });
      alert('Office Location deleted!');
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Office Location delete failed');
    } finally {
      setLoading(false);
    }
  };

  const startTransferStock = (item = null) => {
    if (!canTransferStock) return alert('Only assigned employee or admin can transfer stock');
    resetTransferForm();
    setTransferMode('bulk');
    if (item?.id) {
      setBulkFromWarehouseId(item.warehouse_id ? String(item.warehouse_id) : '');
      setBulkSelectedStockIds([String(item.id)]);
    }
    setInventoryPage('transferStock');
    setShowTransferForm(true);
    setShowWarehouseForm(false);
    setShowStockForm(false);
    setViewMode('stock');
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!transferInventoryId) return alert('Please select a stock item');
    if (!transferToWarehouseId && !transferNewWarehouseName.trim()) return alert('Please select or create a destination office location');
    if (currentUser?.role === 'employee' && !transferReceiverUserId && receiverOptions.length === 0) return alert('Assign an employee to the destination office location');
    if (selectedTransferItem?.warehouse_id && transferToWarehouseId && String(selectedTransferItem.warehouse_id) === String(transferToWarehouseId)) {
      return alert('Source and destination office location cannot be same');
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/inventory/stock-transfers`, {
        inventory_id: transferInventoryId,
        to_warehouse_id: transferToWarehouseId || null,
        receiver_user_id: transferReceiverUserId || null,
        to_warehouse: transferNewWarehouseName.trim(),
        note: transferNote
      }, { headers: getHeaders() });
      alert('Stock transferred!');
      resetTransferForm();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Stock transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkStockSelection = (id) => {
    const stockId = String(id);
    setBulkSelectedStockIds((current) => (
      current.includes(stockId) ? current.filter((item) => item !== stockId) : [...current, stockId]
    ));
  };

  const handleBulkTransferSubmit = async (e) => {
    e.preventDefault();
    if (!bulkFromWarehouseId) return alert('Please select source office location');
    if (!bulkToWarehouseId) return alert('Please select destination office location');
    if (String(bulkFromWarehouseId) === String(bulkToWarehouseId)) return alert('Source and destination office location cannot be same');
    if (bulkSelectedStockIds.length === 0) return alert('Please select stock for transfer');
    if (currentUser?.role === 'employee' && !bulkReceiverUserId && bulkReceiverOptions.length === 0) return alert('Assign an employee to the destination office location');

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/stock-transfers/bulk`, {
        from_warehouse_id: bulkFromWarehouseId,
        to_warehouse_id: bulkToWarehouseId,
        receiver_user_id: bulkReceiverUserId || null,
        inventory_ids: bulkSelectedStockIds,
        note: bulkTransferNote
      }, { headers: getHeaders() });
      alert(res.data?.message || 'Stock transferred!');
      resetTransferForm();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk stock transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const startAssignWarehouse = (item) => {
    if (!canModifyInventory) return alert('Only admin can assign office location employees');
    setAssignmentWarehouseId(String(item.id));
    const assignedIds = warehouseAssignments
      .filter((assignment) => String(assignment.warehouse_id) === String(item.id))
      .map((assignment) => String(assignment.employee_id));
    setAssignmentEmployeeIds(assignedIds);
    setInventoryPage('warehouse');
    setShowWarehouseForm(false);
    setShowTransferForm(false);
  };

  const toggleAssignmentEmployee = (employeeId) => {
    const id = String(employeeId);
    setAssignmentEmployeeIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const saveWarehouseAssignment = async () => {
    if (!assignmentWarehouseId) return alert('Please select an office location');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/inventory/warehouses/${assignmentWarehouseId}/employees`, {
        employee_ids: assignmentEmployeeIds
      }, { headers: getHeaders() });
      alert('Office location employees assigned!');
      setAssignmentWarehouseId('');
      setAssignmentEmployeeIds([]);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Office location assignment failed');
    } finally {
      setLoading(false);
    }
  };

  const openWarehouseAssignmentPanel = () => {
    if (!canModifyInventory) return alert('Only admin can assign office location employees');
    if (warehouses.length === 0) {
      openInventoryForm('warehouse', 'warehouse');
      return alert('Create an office location first, then assign employees');
    }
    closeInventoryForms();
    setInventoryPage('warehouse');
    setViewMode('warehouses');
    setStatusFilter('all');
    setSearchTerm('');
    if (warehouses.length === 1) {
      startAssignWarehouse(warehouses[0]);
    } else {
      setAssignmentWarehouseId('');
      setAssignmentEmployeeIds([]);
    }
  };

  const decideTransfer = async (transfer, decision) => {
    const label = decision === 'accept' ? 'accept' : decision === 'reject' ? 'reject' : 'cancel';
    if (!window.confirm(`Transfer stock to ${label}?`)) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/inventory/stock-transfers/${transfer.id}/${decision}`, {
        note: transferNote
      }, { headers: getHeaders() });
      alert(`Transfer ${label}ed!`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Transfer update failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleAcceptTransferSelection = (transferId) => {
    const id = String(transferId);
    setSelectedAcceptTransferIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const acceptTransfersBulk = async (transferIds) => {
    const ids = [...new Set(transferIds.map(String))]
      .map((id) => stockTransfers.find((item) => String(item.id) === id))
      .filter((item) => item && canAcceptTransfer(item))
      .map((item) => item.id);

    if (ids.length === 0) return alert('No eligible pending transfer selected');
    if (!window.confirm(`Accept ${ids.length} pending stock transfer(s)?`)) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/stock-transfers/bulk-accept`, {
        transfer_ids: ids,
        note: transferNote
      }, { headers: getHeaders() });
      const skippedText = res.data?.skipped_count ? ` Skipped: ${res.data.skipped_count}.` : '';
      alert(`${res.data?.message || 'Stock transfers accepted!'}${skippedText}`);
      setSelectedAcceptTransferIds([]);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Bulk accept failed');
    } finally {
      setLoading(false);
    }
  };

  const acceptSelectedTransfers = () => acceptTransfersBulk(selectedAcceptTransferIds);
  const acceptAllVisiblePendingTransfers = () => acceptTransfersBulk(filteredStockTransfers.filter(canAcceptTransfer).map((item) => item.id));

  const downloadSample = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bulk/sample/stock`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sample_stock.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      downloadLocalStockSample();
    }
  };

  const inp = {
    width: '100%', padding: '10px',
    border: '1px solid #d1d5db', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box'
  };

  const lbl = {
    display: 'block', marginBottom: '6px',
    fontSize: '13px', fontWeight: 'bold', color: '#374151'
  };

  const fillStockFromDeviceName = (value) => {
    const selectedDevice = deviceNames.find(d => d.name === value);
    setDeviceName(value);
    if (selectedDevice?.device_oem_name) setDeviceOemName(selectedDevice.device_oem_name);
    if (selectedDevice?.device_type) setStockDeviceType(selectedDevice.device_type);
  };

  const fillVendor = (vendor) => {
    setVendorName(vendor.name || '');
    setVendorMobile(vendor.mobile || '');
  };

  const inventoryReturnLabel = (item) => {
    const name = item.device_name || item.product_name || item.name || 'Inventory item';
    const deviceNo = item.imei || item.sim_no || item.serial_no || `#${item.id}`;
    return `${name} | ${deviceNo} | ${item.status}`;
  };

  const findCustomerForInventory = (item) => customers.find((customer) => {
    const customerKeys = [customer.imei, customer.sim_no, customer.vehicle_no].filter(Boolean).map(String);
    const inventoryKeys = [item.imei, item.sim_no, item.sim_no_2, item.serial_no].filter(Boolean).map(String);
    return inventoryKeys.some((key) => customerKeys.includes(key));
  });

  const applyReturnInventorySelection = (id) => {
    setReturnInventoryId(id);
    const item = stock.find((row) => String(row.id) === String(id));
    if (!item) return;

    const matchedCustomer = findCustomerForInventory(item);
    if (matchedCustomer) setReturnCustomerId(String(matchedCustomer.id));
    const matchedOem = deviceOems.find((oem) => String(oem.name || '').toLowerCase() === String(item.device_oem_name || '').toLowerCase());
    if (matchedOem) setReturnSendToOemId(String(matchedOem.id));
    if (!returnReason) {
      setReturnReason(`${item.device_name || item.name || 'Device'} return`);
    }
  };

  const getInventoryVendorName = (item) => {
    const name = String(item?.vendor_name || '').trim();
    if (name) return name;
    return item?.purchase_id ? 'Direct Purchase' : 'No Vendor';
  };

  const getVendorKey = (name) => `vendor:${String(name || '').trim().toLowerCase() || 'no vendor'}`;
  const selectedVendorKey = canViewPurchaseDetails ? vendorFilter : 'all';
  const matchesSelectedVendor = (item) => selectedVendorKey === 'all'
    || getVendorKey(getInventoryVendorName(item)) === selectedVendorKey;

  const vendorOptionMap = new Map();
  const addVendorOption = (name) => {
    const label = String(name || '').trim();
    if (!label) return;
    const key = getVendorKey(label);
    if (!vendorOptionMap.has(key)) vendorOptionMap.set(key, { key, label });
  };
  vendors.forEach((vendor) => addVendorOption(vendor.name));
  stock.forEach((item) => addVendorOption(getInventoryVendorName(item)));
  returns.forEach((item) => addVendorOption(getInventoryVendorName(item)));
  const vendorOptions = Array.from(vendorOptionMap.values())
    .sort((a, b) => a.label.localeCompare(b.label));
  const selectedVendorLabel = selectedVendorKey === 'all'
    ? 'All Vendors'
    : vendorOptions.find((vendor) => vendor.key === selectedVendorKey)?.label || 'Selected Vendor';
  const warehouseOptionMap = new Map();
  warehouses.forEach((item) => {
    const name = String(item.name || '').trim();
    if (name) warehouseOptionMap.set(`id:${item.id}`, { id: item.id, name, admin_id: item.admin_id });
  });
  stock.forEach((item) => {
    const name = String(item.warehouse || '').trim();
    if (name && !Array.from(warehouseOptionMap.values()).some((warehouseItem) => warehouseItem.name.toLowerCase() === name.toLowerCase())) {
      warehouseOptionMap.set(`name:${name.toLowerCase()}`, { id: item.warehouse_id || '', name, admin_id: item.admin_id });
    }
  });
  const warehouseOptions = Array.from(warehouseOptionMap.values())
    .sort((a, b) => a.name.localeCompare(b.name));
  const isTransferPending = (item) => Boolean(item?.transfer_status && item.transfer_status !== 'none');
  const transferableStock = stock.filter((item) => item.status !== 'sold' && !isTransferPending(item));
  const selectedTransferItem = stock.find((item) => String(item.id) === String(transferInventoryId));
  const employeeOptions = users
    .filter((item) => item.role === 'employee' && Number(item.is_active ?? 1) === 1)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const employeesForAssignment = assignmentWarehouseId
    ? employeeOptions.filter((employee) => {
      const selectedWarehouse = warehouses.find((item) => String(item.id) === String(assignmentWarehouseId));
      return currentUser?.role === 'superadmin'
        ? Number(employee.created_by || 0) === Number(selectedWarehouse?.admin_id || 0)
        : true;
    })
    : employeeOptions;
  const receiverOptions = transferToWarehouseId
    ? warehouseAssignments
      .filter((assignment) => String(assignment.warehouse_id) === String(transferToWarehouseId))
      .map((assignment) => ({
      id: assignment.employee_id,
      name: assignment.employee_name,
      mobile: assignment.employee_mobile
    }))
    : [];
  const transferWarehouseMap = new Map();
  warehouses.forEach((item) => transferWarehouseMap.set(String(item.id), item));
  warehouseAssignments.forEach((assignment) => {
    if (assignment.warehouse_id && !transferWarehouseMap.has(String(assignment.warehouse_id))) {
      transferWarehouseMap.set(String(assignment.warehouse_id), {
        id: assignment.warehouse_id,
        name: assignment.warehouse_name,
        admin_id: assignment.admin_id,
        total_stock: null
      });
    }
  });
  const transferWarehouseOptions = Array.from(transferWarehouseMap.values())
    .filter((item) => item.id && item.name)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const warehouseScopeId = (item) => String(item?.admin_id ?? '');
  const warehouseOptionLabel = (item) => {
    const name = String(item?.name || '').trim() || 'Office Location';
    if (currentUser?.role !== 'superadmin') return name;
    const owner = String(item?.admin_name || '').trim()
      || (item?.admin_id !== undefined && item?.admin_id !== null ? `Admin #${item.admin_id}` : '');
    return owner ? `${name} - ${owner}` : name;
  };
  const selectedTransferSourceWarehouse = selectedTransferItem?.warehouse_id
    ? transferWarehouseOptions.find((item) => String(item.id) === String(selectedTransferItem.warehouse_id))
    : null;
  const transferDestinationWarehouseOptions = selectedTransferSourceWarehouse
    ? transferWarehouseOptions
      .filter((item) => String(item.id) !== String(selectedTransferSourceWarehouse.id))
      .filter((item) => warehouseScopeId(item) === warehouseScopeId(selectedTransferSourceWarehouse))
    : [];
  const selectedTransferToWarehouse = transferDestinationWarehouseOptions.find((item) => String(item.id) === String(transferToWarehouseId));
  const compactDigits = (value, length = 5) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits ? digits.slice(-length) : '';
  };
  const bulkStockLabel = (item) => {
    const name = item.device_name || item.product_name || item.name || 'Inventory item';
    const imei = compactDigits(item.imei);
    const sim = compactDigits(item.sim_no || item.sim_no_2 || item.serial_no);
    return `${name}${imei ? ` | IMEI ...${imei}` : ''}${sim ? ` | SIM ...${sim}` : ''}`;
  };
  const stockMatchesBulkSearch = (item, query) => {
    const term = String(query || '').trim().toLowerCase();
    if (!term) return true;
    const termDigits = term.replace(/\D/g, '');
    const numericText = [item.imei, item.sim_no, item.sim_no_2, item.serial_no].join(' ').replace(/\D/g, '');
    if (termDigits.length >= 3 && numericText.includes(termDigits)) return true;
    return [
      item.device_name,
      item.product_name,
      item.name,
      item.device_oem_name,
      item.device_type,
      item.imei,
      item.sim_no,
      item.sim_no_2,
      item.serial_no,
      item.warehouse
    ].join(' ').toLowerCase().includes(term);
  };
  const selectedReturnItem = stock.find((item) => String(item.id) === String(returnInventoryId));
  const returnEntryStockMatches = stock.filter((item) => stockMatchesBulkSearch(item, returnEntrySearch));
  const returnEntryStockOptions = selectedReturnItem && !returnEntryStockMatches.some((item) => String(item.id) === String(selectedReturnItem.id))
    ? [selectedReturnItem, ...returnEntryStockMatches]
    : returnEntryStockMatches;
  const bulkSelectedIdSet = new Set(bulkSelectedStockIds.map(String));
  const bulkSourceWarehouseOptions = transferWarehouseOptions
    .filter((warehouseItem) => transferableStock.some((item) => String(item.warehouse_id) === String(warehouseItem.id)));
  const selectedBulkFromWarehouse = transferWarehouseOptions.find((item) => String(item.id) === String(bulkFromWarehouseId));
  const bulkDestinationWarehouseOptions = selectedBulkFromWarehouse
    ? transferWarehouseOptions
      .filter((item) => String(item.id) !== String(selectedBulkFromWarehouse.id))
      .filter((item) => warehouseScopeId(item) === warehouseScopeId(selectedBulkFromWarehouse))
    : [];
  const bulkAvailableRows = transferableStock
    .filter((item) => bulkFromWarehouseId && String(item.warehouse_id) === String(bulkFromWarehouseId))
    .filter((item) => !bulkSelectedIdSet.has(String(item.id)))
    .filter((item) => stockMatchesBulkSearch(item, bulkTransferSearch));
  const bulkSelectedRows = bulkSelectedStockIds
    .map((id) => stock.find((item) => String(item.id) === String(id)))
    .filter(Boolean);
  const bulkReceiverOptions = bulkToWarehouseId
    ? warehouseAssignments
      .filter((assignment) => String(assignment.warehouse_id) === String(bulkToWarehouseId))
      .map((assignment) => ({
        id: assignment.employee_id,
        name: assignment.employee_name,
        mobile: assignment.employee_mobile
      }))
    : [];
  const selectedBulkToWarehouse = bulkDestinationWarehouseOptions.find((item) => String(item.id) === String(bulkToWarehouseId));
  const isCurrentEmployeeAssignedToWarehouse = (warehouseId) => currentUser?.role === 'employee'
    && warehouseAssignments.some((assignment) => (
      String(assignment.warehouse_id) === String(warehouseId)
      && Number(assignment.employee_id) === Number(currentUser?.id || 0)
    ));
  const canAcceptTransfer = (transfer) => transfer.status === 'pending' && (
    canModifyInventory || Number(transfer.receiver_user_id || 0) === Number(currentUser?.id || 0)
    || isCurrentEmployeeAssignedToWarehouse(transfer.to_warehouse_id)
  );
  const canRejectTransfer = canAcceptTransfer;
  const canCancelTransfer = (transfer) => transfer.status === 'pending' && (
    canModifyInventory || Number(transfer.transferred_by || 0) === Number(currentUser?.id || 0)
  );
  const filteredStockTransfers = stockTransfers.filter((item) => {
    const matchesStatus = transferStatusFilter === 'all' || item.status === transferStatusFilter;
    const term = transferSearchTerm.trim().toLowerCase();
    if (!matchesStatus) return false;
    if (!term) return true;

    const termDigits = term.replace(/\D/g, '');
    const imeiDigits = String(item.imei || '').replace(/\D/g, '');
    const numericText = [
      item.imei,
      item.sim_no,
      item.serial_no
    ].join(' ').replace(/\D/g, '');
    const haystack = [
      item.device_name,
      item.device_oem_name,
      item.device_type,
      item.imei,
      item.sim_no,
      item.serial_no,
      item.from_warehouse,
      item.to_warehouse,
      item.status,
      item.receiver_name,
      item.receiver_mobile,
      item.transferred_by_name,
      item.accepted_by_name,
      item.accepted_by_mobile,
      item.note
    ].join(' ').toLowerCase();

    return haystack.includes(term)
      || (termDigits.length >= 3 && numericText.includes(termDigits))
      || (termDigits.length >= 5 && imeiDigits.endsWith(termDigits));
  });
  const eligibleFilteredTransfers = filteredStockTransfers.filter(canAcceptTransfer);
  const selectedAcceptRows = selectedAcceptTransferIds
    .map((id) => stockTransfers.find((item) => String(item.id) === String(id)))
    .filter((item) => item && canAcceptTransfer(item));

  useEffect(() => {
    setSelectedAcceptTransferIds((current) => current.filter((id) => {
      const transfer = stockTransfers.find((item) => String(item.id) === String(id));
      return transfer && canAcceptTransfer(transfer);
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockTransfers, currentUser?.id, currentUser?.role]);

  const vendorStock = canViewPurchaseDetails && selectedVendorKey !== 'all'
    ? stock.filter(matchesSelectedVendor)
    : stock;
  const vendorReturns = canViewPurchaseDetails && selectedVendorKey !== 'all'
    ? returns.filter(matchesSelectedVendor)
    : returns;
  const lowStockMatchesProduct = (lowItem, stockItem) => (
    (lowItem.product_id && String(lowItem.product_id) === String(stockItem.product_id))
    || (!lowItem.product_id && lowItem.name === (stockItem.product_name || stockItem.name) && lowItem.type === (stockItem.product_type || stockItem.type))
  );
  const visibleLowStock = canViewPurchaseDetails && selectedVendorKey !== 'all'
    ? lowStock.filter((lowItem) => vendorStock.some((item) => lowStockMatchesProduct(lowItem, item)))
    : lowStock;
  const availableRows = vendorStock.filter(s => s.status === 'available');
  const soldRows = vendorStock.filter(s => s.status === 'sold');
  const defectiveRows = vendorStock.filter(s => s.status === 'defective');
  const purchaseInventory = vendorStock.filter(s => s.purchase_id);
  const available = availableRows.length;
  const sold = soldRows.length;
  const defective = defectiveRows.length;
  const filteredStock = vendorStock.filter((item) => {
    const search = searchTerm.trim().toLowerCase();
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const haystack = [
      item.device_name,
      item.name,
      item.device_oem_name,
      item.device_type,
      item.imei,
      item.sim_no,
      item.sim_no_2,
      item.serial_no,
      item.warehouse,
      item.sale_price,
      item.sale_total_amount,
      ...(canViewPurchaseDetails ? [item.vendor_name, item.invoice_no] : [])
    ].join(' ').toLowerCase();
    return matchesStatus && (!search || haystack.includes(search));
  });
  const visibleStock = viewMode === 'purchase'
    ? filteredStock.filter((item) => item.purchase_id)
    : filteredStock;
  const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const downloadCsvRows = (rows, fileName) => {
    const csv = rows.map(row => row.map(csvCell).join(',')).join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };
  const downloadOfficeLocationStock = (warehouseItem, status = 'all') => {
    const warehouseId = String(warehouseItem?.id ?? warehouseItem?.warehouse_id ?? '');
    const warehouseName = String(warehouseItem?.name ?? warehouseItem?.warehouse ?? '').trim();
    const warehouseNameKey = warehouseName.toLowerCase();
    const warehouseAdminId = String(warehouseItem?.admin_id ?? '');
    const rows = stock.filter((item) => {
      const itemWarehouseId = String(item?.warehouse_id ?? item?.office_location_id ?? '');
      const itemWarehouseName = String(item?.warehouse ?? item?.office_location ?? '').trim().toLowerCase();
      const itemAdminId = String(item?.admin_id ?? '');
      const sameOfficeLocation = (warehouseId && itemWarehouseId && warehouseId === itemWarehouseId)
        || (warehouseNameKey && itemWarehouseName && warehouseNameKey === itemWarehouseName);
      const sameAdmin = !warehouseAdminId || !itemAdminId || warehouseAdminId === itemAdminId;
      const sameStatus = status === 'all' || item.status === status;
      return sameOfficeLocation && sameAdmin && sameStatus;
    });
    const header = [
      '#',
      'Device Name',
      'Device OEM Name',
      'Device Type',
      'IMEI Number',
      'SIM Number 1',
      'SIM Number 2',
      'Serial No',
      'Office Location',
      ...(canViewPurchaseDetails ? ['Vendor', 'Invoice', 'Purchase Date', 'Purchase Price'] : []),
      'Status'
    ];
    const dataRows = rows.map((item, index) => [
      index + 1,
      item.device_name || item.name || '',
      item.device_oem_name || '',
      item.device_type || item.type || '',
      item.imei || '',
      item.sim_no || '',
      item.sim_no_2 || '',
      item.serial_no || '',
      item.warehouse || warehouseName || '',
      ...(canViewPurchaseDetails ? [
        item.vendor_name || '',
        item.invoice_no || '',
        item.purchase_date ? formatDate(item.purchase_date) : '',
        item.purchase_price || 0
      ] : []),
      item.status || ''
    ]);
    const safeName = `${warehouseName || 'office-location'}-${status === 'all' ? 'total' : status}-stock`
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
    downloadCsvRows([header, ...dataRows], `${safeName || 'office-location-stock'}.csv`);
  };
  const renderWarehouseStockDownload = (warehouseItem, status, count, color) => (
    <button
      type="button"
      onClick={() => downloadOfficeLocationStock(warehouseItem, status)}
      title={`Download ${status === 'all' ? 'total' : status} stock`}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        color,
        cursor: 'pointer',
        font: 'inherit',
        fontWeight: 'bold',
        textDecoration: 'underline',
        textUnderlineOffset: '3px'
      }}
    >
      {count || 0}
    </button>
  );
  const filteredReturns = vendorReturns.filter((item) => {
    const search = searchTerm.trim().toLowerCase();
    const haystack = [
      item.product_name,
      item.product_type,
      item.imei,
      item.serial_no,
      item.customer_name,
      item.customer_mobile,
      item.vehicle_no,
      item.received_from_customer_name,
      item.received_from_customer_mobile,
      item.received_from_vehicle_no,
      item.send_to_oem_display_name,
      item.send_to_oem_name,
      item.condition_status,
      item.reason,
      item.returned_by_name,
      ...(canViewPurchaseDetails ? [item.vendor_name] : [])
    ].join(' ').toLowerCase();
    return !search || haystack.includes(search);
  });
  const buildVendorBreakdown = (items) => {
    const totals = new Map();
    items.forEach((item) => {
      const label = getInventoryVendorName(item);
      const key = getVendorKey(label);
      const current = totals.get(key) || { label, count: 0 };
      current.count += 1;
      totals.set(key, current);
    });
    return Array.from(totals.values()).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  };
  const vendorDetailText = (items) => {
    if (!canViewPurchaseDetails) return '';
    if (selectedVendorKey !== 'all') return `${selectedVendorLabel}: ${items.length}`;
    const breakdown = buildVendorBreakdown(items);
    if (breakdown.length === 0) return 'No vendor data';
    const topVendors = breakdown.slice(0, 2).map((vendor) => `${vendor.label}: ${vendor.count}`).join(' | ');
    return breakdown.length > 2 ? `${topVendors} | +${breakdown.length - 2}` : topVendors;
  };

  const activeInventoryForm = showStockForm ? 'stock'
    : showBulkForm ? 'bulk'
      : showReturnForm ? 'return'
        : showTransferForm ? 'transfer'
          : showWarehouseForm ? 'warehouse'
            : showDeviceNameForm ? 'deviceName'
              : showOemForm ? 'oem'
                : showTypeForm ? 'type'
                  : showProductForm ? 'product'
                    : '';

  const closeInventoryForms = () => {
    setShowTypeForm(false);
    setShowOemForm(false);
    setShowDeviceNameForm(false);
    setShowProductForm(false);
    setShowStockForm(false);
    setShowBulkForm(false);
    setShowReturnForm(false);
    setShowWarehouseForm(false);
    setShowTransferForm(false);
  };

  useEffect(() => {
    closeInventoryForms();
    resetStockForm();
    resetWarehouseForm();
    resetTransferForm();
    setInventoryPage(defaultInventoryPage);
    setViewMode('stock');
    setStatusFilter('all');
    setSearchTerm('');
    setTransferSearchTerm('');
    setReturnEntrySearch('');
    setTransferStatusFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultInventoryPage]);

  const openInventoryForm = (form, pageKey = form) => {
    const shouldClose = activeInventoryForm === form && inventoryPage === pageKey;
    resetStockForm();
    resetTransferForm();
    if (form !== 'warehouse') resetWarehouseForm();
    if (form !== 'return' || shouldClose) setReturnEntrySearch('');
    setInventoryPage(shouldClose ? defaultInventoryPage : pageKey);
    setShowTypeForm(!shouldClose && form === 'type');
    setShowOemForm(!shouldClose && form === 'oem');
    setShowDeviceNameForm(!shouldClose && form === 'deviceName');
    setShowProductForm(!shouldClose && form === 'product');
    setShowStockForm(!shouldClose && form === 'stock');
    setShowBulkForm(!shouldClose && form === 'bulk');
    setShowReturnForm(!shouldClose && form === 'return');
    setShowWarehouseForm(!shouldClose && form === 'warehouse');
    setShowTransferForm(!shouldClose && form === 'transfer');
    setViewMode(form === 'return' ? 'returns' : 'stock');
  };

  const openInventoryView = (mode, status = 'all', pageKey = mode) => {
    closeInventoryForms();
    setInventoryPage(pageKey);
    setViewMode(mode);
    setStatusFilter(status);
    setSearchTerm('');
    setReturnEntrySearch('');
  };

  useEffect(() => {
    if (!sidebarInventoryAction) return;
    if (sidebarInventoryAction.type === 'view') {
      openInventoryView(
        sidebarInventoryAction.mode,
        sidebarInventoryAction.status,
        sidebarInventoryAction.pageKey
      );
    } else if (sidebarInventoryAction.type === 'form') {
      openInventoryForm(sidebarInventoryAction.form, sidebarInventoryAction.pageKey);
    } else if (sidebarInventoryAction.type === 'transfer') {
      startTransferStock();
    } else if (sidebarInventoryAction.type === 'assign') {
      openWarehouseAssignmentPanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryView]);

  const inventoryTileThemes = {
    navy: { tone: '#1e3a5f', tone2: '#0ea5e9', soft: '#e0f2fe', gradient: 'linear-gradient(135deg, #f8fafc 0%, #dbeafe 100%)', shadow: 'rgba(30, 58, 95, 0.22)' },
    sky: { tone: '#0284c7', tone2: '#22d3ee', soft: '#cffafe', gradient: 'linear-gradient(135deg, #f0f9ff 0%, #cffafe 100%)', shadow: 'rgba(2, 132, 199, 0.22)' },
    rose: { tone: '#be123c', tone2: '#f97316', soft: '#ffe4e6', gradient: 'linear-gradient(135deg, #fff1f2 0%, #ffedd5 100%)', shadow: 'rgba(190, 18, 60, 0.22)' },
    teal: { tone: '#0f766e', tone2: '#06b6d4', soft: '#ccfbf1', gradient: 'linear-gradient(135deg, #f0fdfa 0%, #cffafe 100%)', shadow: 'rgba(15, 118, 110, 0.22)' },
    emerald: { tone: '#047857', tone2: '#22c55e', soft: '#d1fae5', gradient: 'linear-gradient(135deg, #ecfdf5 0%, #bbf7d0 100%)', shadow: 'rgba(4, 120, 87, 0.22)' },
    blue: { tone: '#2563eb', tone2: '#6366f1', soft: '#dbeafe', gradient: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)', shadow: 'rgba(37, 99, 235, 0.22)' },
    violet: { tone: '#7c3aed', tone2: '#ec4899', soft: '#ede9fe', gradient: 'linear-gradient(135deg, #f5f3ff 0%, #fce7f3 100%)', shadow: 'rgba(124, 58, 237, 0.22)' },
    amber: { tone: '#b45309', tone2: '#f59e0b', soft: '#fef3c7', gradient: 'linear-gradient(135deg, #fffbeb 0%, #fed7aa 100%)', shadow: 'rgba(180, 83, 9, 0.22)' },
    slate: { tone: '#475569', tone2: '#64748b', soft: '#e2e8f0', gradient: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', shadow: 'rgba(71, 85, 105, 0.2)' },
    cyan: { tone: '#0891b2', tone2: '#14b8a6', soft: '#cffafe', gradient: 'linear-gradient(135deg, #ecfeff 0%, #ccfbf1 100%)', shadow: 'rgba(8, 145, 178, 0.22)' },
    lime: { tone: '#4d7c0f', tone2: '#84cc16', soft: '#ecfccb', gradient: 'linear-gradient(135deg, #f7fee7 0%, #d9f99d 100%)', shadow: 'rgba(77, 124, 15, 0.22)' }
  };

  const topInventoryOptions = [
    { key: 'availableTop', title: 'Available', value: available, detail: vendorDetailText(availableRows), color: '#10b981', theme: 'emerald', Icon: PackageCheck, activeKeys: ['available'], action: () => openInventoryView('stock', 'available', 'available') },
    { key: 'soldTop', title: 'Sold', value: sold, detail: vendorDetailText(soldRows), color: '#3b82f6', theme: 'blue', Icon: Send, activeKeys: ['sold'], action: () => openInventoryView('stock', 'sold', 'sold') },
    { key: 'defectiveTop', title: 'Defective', value: defective, detail: vendorDetailText(defectiveRows), color: '#ef4444', theme: 'rose', Icon: AlertTriangle, activeKeys: ['defective'], action: () => openInventoryView('stock', 'defective', 'defective') },
    ...(canViewPurchaseDetails ? [{ key: 'lowStockTop', title: 'Low Stock', value: visibleLowStock.length, detail: vendorDetailText(vendorStock.filter((item) => visibleLowStock.some((lowItem) => lowStockMatchesProduct(lowItem, item)))), color: '#f59e0b', theme: 'amber', Icon: AlertTriangle, activeKeys: ['lowStock'], action: () => openInventoryView('stock', 'all', 'lowStock') }] : []),
    ...(canViewPurchaseDetails ? [{ key: 'purchaseTop', title: 'Purchase Inventory', value: purchaseInventory.length, detail: vendorDetailText(purchaseInventory), color: '#0ea5e9', theme: 'sky', Icon: FileSpreadsheet, activeKeys: ['purchase'], action: () => openInventoryView('purchase', 'all', 'purchase') }] : []),
    ...(canViewPurchaseDetails ? [{ key: 'returnsTop', title: 'Inventory Returns', value: vendorReturns.length, detail: vendorDetailText(vendorReturns), color: '#dc2626', theme: 'rose', Icon: RotateCcw, activeKeys: ['returns'], action: () => openInventoryView('returns', 'all', 'returns') }] : [])
  ];
  const isInventoryDashboardPage = inventoryPage === 'dashboard';
  const isInventoryModulePage = inventoryPage === 'module';
  const isLiveInventoryReportPage = inventoryPage === 'home';
  const isInventoryDetailPage = !isInventoryDashboardPage && !isInventoryModulePage && !isLiveInventoryReportPage;
  const inventoryTablePages = ['stock', 'purchase', 'returns', 'available', 'sold', 'defective'];
  const isInventoryTileActive = (tile) => {
    const activeKeys = tile.activeKeys || [tile.key];
    return activeKeys.includes(inventoryPage) || activeKeys.includes(statusFilter) || activeInventoryForm === tile.form;
  };
  const compactInventoryGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '8px',
    alignItems: 'stretch'
  };
  const renderInventoryTile = (tile, { compact = false } = {}) => {
    const theme = inventoryTileThemes[tile.theme] || inventoryTileThemes.navy;
    const Icon = tile.Icon || Boxes;
    const isActive = isInventoryTileActive(tile);
    const valueText = tile.value ?? tile.count ?? 'OPEN';
    const valueLength = String(valueText).length;
    const actionButtons = tile.quickActions || [];
    const filterButtons = tile.filterActions || [];
    const cardStyle = {
      minHeight: compact ? (actionButtons.length || filterButtons.length ? '112px' : '64px') : (actionButtons.length || filterButtons.length ? '122px' : '78px'),
      padding: compact ? '8px 9px' : '10px',
      borderRadius: '8px',
      border: `1px solid ${isActive ? theme.tone : 'rgba(148, 163, 184, 0.35)'}`,
      cursor: actionButtons.length > 0 || filterButtons.length > 0 ? 'default' : tile.action ? 'pointer' : 'default',
      background: isActive ? theme.gradient : `linear-gradient(135deg, #ffffff 0%, ${theme.soft} 100%)`,
      color: '#0f172a',
      boxShadow: isActive ? `0 8px 16px ${theme.shadow}` : '0 4px 10px rgba(15,23,42,0.06)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: compact ? '6px' : '8px',
      textAlign: 'left',
      position: 'relative',
      overflow: 'hidden',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease'
    };
    const content = (
      <>
        <span style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '3px', background: `linear-gradient(90deg, ${theme.tone}, ${theme.tone2})` }} />
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px', width: '100%' }}>
          <span style={{
            width: compact ? '26px' : '28px',
            height: compact ? '26px' : '28px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${theme.tone}, ${theme.tone2})`,
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
            boxShadow: `0 4px 10px ${theme.shadow}`,
            position: 'relative',
            zIndex: 1
          }}>
            <Icon size={compact ? 14 : 15} strokeWidth={2.2} />
          </span>
          <span style={{
            minWidth: valueLength > 3 ? '32px' : '24px',
            maxWidth: '54px',
            padding: '2px 6px',
            borderRadius: '999px',
            background: isActive ? theme.tone : 'rgba(255,255,255,0.78)',
            color: isActive ? 'white' : theme.tone,
            border: `1px solid ${isActive ? theme.tone : 'rgba(255,255,255,0.92)'}`,
            fontSize: valueLength > 3 ? '9px' : '10px',
            fontWeight: 'bold',
            textAlign: 'center',
            boxShadow: '0 2px 6px rgba(15,23,42,0.07)',
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {valueText}
          </span>
        </span>
        <span style={{ display: 'block', position: 'relative', zIndex: 1 }}>
          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: compact ? '11px' : '12px', fontWeight: 800, lineHeight: 1.15, color: isActive ? theme.tone : '#0f172a' }}>
            {tile.title || tile.label}
          </span>
          {tile.detail && (
            <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginTop: '3px', color: '#475569', fontSize: compact ? '9px' : '10px', lineHeight: 1.2, wordBreak: 'break-word' }}>
              {tile.detail}
            </span>
          )}
        </span>
        {actionButtons.length > 0 && (
          <span style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px', width: '100%', position: 'relative', zIndex: 2 }}>
            {actionButtons.map((item) => {
              const ActionIcon = item.Icon || Eye;
              const isDownload = item.label === 'Download';
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    item.action?.();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '3px',
                    minWidth: 0,
                    minHeight: '24px',
                    padding: '0 6px',
                    borderRadius: '6px',
                    border: `1px solid ${isDownload ? theme.tone : 'rgba(148, 163, 184, 0.38)'}`,
                    background: isDownload ? theme.tone : 'rgba(255,255,255,0.88)',
                    color: isDownload ? 'white' : theme.tone,
                    cursor: 'pointer',
                    fontSize: '10px',
                    fontWeight: 800,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(15,23,42,0.07)'
                  }}
                >
                  <ActionIcon size={11} strokeWidth={2.4} />
                  {item.label}
                </button>
              );
            })}
          </span>
        )}
        {filterButtons.length > 0 && (
          <span style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '5px', width: '100%', position: 'relative', zIndex: 2 }}>
            {filterButtons.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  item.action?.();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '4px',
                  minWidth: 0,
                  minHeight: '24px',
                  padding: '0 6px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.tone}`,
                  background: 'rgba(255,255,255,0.9)',
                  color: theme.tone,
                  cursor: 'pointer',
                  fontSize: '9px',
                  fontWeight: 800,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 6px rgba(15,23,42,0.07)'
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                <strong>{item.value}</strong>
              </button>
            ))}
          </span>
        )}
      </>
    );
    if (actionButtons.length > 0 || filterButtons.length > 0) {
      return (
        <div key={tile.key} style={cardStyle}>
          {content}
        </div>
      );
    }
    return (
      <button key={tile.key} type="button" onClick={tile.action} style={cardStyle}>
        {content}
      </button>
    );
  };

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      {canViewPurchaseDetails && isInventoryDetailPage && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          background: '#eef7ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '14px'
        }}>
          <div>
            <span style={{ display: 'block', color: '#2563eb', fontSize: '12px', fontWeight: 'bold' }}>Vendor</span>
            <strong style={{ color: '#0f172a', fontSize: '15px' }}>{selectedVendorLabel}</strong>
          </div>
          <select
            style={{ ...inp, maxWidth: '280px', background: 'white' }}
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
          >
            <option value="all">All Vendors</option>
            {vendorOptions.map((vendor) => (
              <option key={vendor.key} value={vendor.key}>{vendor.label}</option>
            ))}
          </select>
        </div>
      )}

      {isInventoryDashboardPage && (
      <>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px 18px',
        marginBottom: '14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderLeft: '5px solid #1e3a5f'
      }}>
        <h3 style={{ margin: 0, color: '#1e3a5f' }}>Inventory Dashboard</h3>
        <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '13px' }}>Live stock status shortcuts.</p>
      </div>
      <div style={{
        ...compactInventoryGridStyle,
        marginBottom: '14px'
      }}>
        {topInventoryOptions.map((item) => renderInventoryTile(item, { compact: true }))}
      </div>
      </>
      )}

      </div>

      <div className="fixed-panel-body">
      {isInventoryDetailPage && (
      <>
      {showBulkForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#f59e0b', marginTop: 0 }}>Bulk Stock Upload</h3>
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#92400e' }}>
              {canViewPurchaseDetails
                ? 'Sample columns include device details plus purchase details: Vendor, Invoice, Payment Mode, Paid Amount and Note.'
                : 'Sample columns include device details only. Purchase price details are admin-only.'}
            </p>
            <button onClick={downloadSample} style={{
              background: '#f59e0b', color: 'white', border: 'none',
              padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
            }}>
              Download Sample Excel
            </button>
          </div>

          <form onSubmit={handleBulkUpload}>
            <div style={{ marginBottom: '16px' }}>
              <label style={lbl}>Select Excel File (.xlsx)</label>
              <input type="file" accept=".xlsx,.xls"
                onChange={e => setBulkFile(e.target.files[0])}
                style={{ ...inp, padding: '8px' }} />
            </div>
            <button type="submit" disabled={loading} style={{
              background: '#10b981', color: 'white', border: 'none',
              padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
            }}>
              {loading ? 'Uploading...' : 'Upload'}
            </button>
          </form>

          {bulkResult && (
            <div style={{
              marginTop: '16px', padding: '12px', borderRadius: '8px',
              background: bulkResult.errors?.length > 0 ? '#fef3c7' : '#d1fae5'
            }}>
              <p style={{ margin: '0 0 4px', fontWeight: 'bold', color: bulkResult.errors?.length > 0 ? '#92400e' : '#065f46' }}>
                {bulkResult.errors?.length > 0 && Number(bulkResult.inserted || 0) === 0
                  ? (bulkResult.message || 'Upload rejected. Fix errors and upload again.')
                  : `${bulkResult.inserted} items added successfully!`}
              </p>
              <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}>
                Total: {bulkResult.total}
              </p>
              {bulkResult.errors?.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 4px' }}>Errors:</p>
                  {bulkResult.errors.map((e, i) => (
                    <p key={i} style={{ color: '#dc2626', fontSize: '12px', margin: '2px 0' }}>{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showReturnForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#dc2626', marginTop: 0 }}>Inventory Return Entry</h3>
          <form onSubmit={handleReturnSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={lbl}>Search Inventory Item</label>
                <input
                  style={{ ...inp, marginBottom: '10px' }}
                  value={returnEntrySearch}
                  onChange={e => setReturnEntrySearch(e.target.value)}
                  placeholder="Search IMEI, SIM, serial, device, OEM or location"
                />
                <label style={lbl}>Fetch Inventory Item *</label>
                <select style={inp} value={returnInventoryId} onChange={e => applyReturnInventorySelection(e.target.value)} required>
                  <option value="">Select item from inventory</option>
                  {returnEntryStockOptions.map(item => (
                    <option key={item.id} value={item.id}>
                      {inventoryReturnLabel(item)}
                    </option>
                  ))}
                </select>
                <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                  Showing {returnEntryStockMatches.length} of {stock.length} inventory items
                </small>
                {selectedReturnItem && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                    {[selectedReturnItem.imei || 'No IMEI', selectedReturnItem.sim_no || selectedReturnItem.sim_no_2 || 'No SIM', canViewPurchaseDetails ? (selectedReturnItem.vendor_name || 'No vendor') : null].filter(Boolean).join(' | ')}
                  </small>
                )}
              </div>
              <div>
                <label style={lbl}>Received From Customer</label>
                <select style={inp} value={returnCustomerId} onChange={e => setReturnCustomerId(e.target.value)}>
                  <option value="">Select customer / optional</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>{customer.name} - {customer.mobile}{customer.vehicle_no ? ` - ${customer.vehicle_no}` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Send To OEM</label>
                <select style={inp} value={returnSendToOemId} onChange={e => setReturnSendToOemId(e.target.value)}>
                  <option value="">Select OEM / optional</option>
                  {deviceOems.map(oem => (
                    <option key={oem.id} value={oem.id}>{oem.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Condition</label>
                <select style={inp} value={returnCondition} onChange={e => setReturnCondition(e.target.value)}>
                  <option value="good">Good - make available</option>
                  <option value="damaged">Damaged - mark defective</option>
                  <option value="defective">Defective - mark defective</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Reason</label>
                <input style={inp} value={returnReason} onChange={e => setReturnReason(e.target.value)} placeholder="Return reason / remarks" />
              </div>
            </div>
            <button type="submit" disabled={loading} style={{
              marginTop: '16px', background: '#dc2626', color: 'white',
              border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
            }}>
              {loading ? 'Saving...' : 'Save Return'}
            </button>
          </form>
        </div>
      )}

      {showWarehouseForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#14b8a6', marginTop: 0 }}>{editWarehouseId ? 'Edit Office Location' : 'Create Office Location'}</h3>
          <form onSubmit={handleWarehouseSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
              <div>
                <label style={lbl}>Office Location Name *</label>
                <input style={inp} value={warehouseName} onChange={e => setWarehouseName(e.target.value)} placeholder="Main / Branch / Field Stock" required />
              </div>
              <div>
                <label style={lbl}>Address</label>
                <input style={inp} value={warehouseAddress} onChange={e => setWarehouseAddress(e.target.value)} placeholder="Office location address" />
              </div>
              <div>
                <label style={lbl}>Note</label>
                <input style={inp} value={warehouseNote} onChange={e => setWarehouseNote(e.target.value)} placeholder="Manager / remarks" />
              </div>
              <button type="submit" disabled={loading} style={{
                background: '#14b8a6', color: 'white',
                border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
              }}>
                {loading ? 'Saving...' : editWarehouseId ? 'Update' : 'Save'}
              </button>
            </div>
            {editWarehouseId && (
              <button type="button" onClick={resetWarehouseForm} style={{
                marginTop: '12px', background: '#f3f4f6', color: '#374151',
                border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
              }}>
                Cancel Edit
              </button>
            )}
          </form>
        </div>
      )}

      {showTransferForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#7c3aed', marginTop: 0 }}>Stock Transfer</h3>
          {transferMode === 'single' ? (
          <form onSubmit={handleTransferSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: '16px', alignItems: 'end' }}>
              <div>
                <InventoryDevicePicker
                  items={transferableStock}
                  value={transferInventoryId}
                  onChange={(id, item) => {
                    setTransferInventoryId(id);
                    const nextSource = item?.warehouse_id
                      ? transferWarehouseOptions.find((warehouseItem) => String(warehouseItem.id) === String(item.warehouse_id))
                      : null;
                    const currentDestination = transferWarehouseOptions.find((warehouseItem) => String(warehouseItem.id) === String(transferToWarehouseId));
                    if (!nextSource || !currentDestination
                      || String(currentDestination.id) === String(nextSource.id)
                      || warehouseScopeId(currentDestination) !== warehouseScopeId(nextSource)) {
                      setTransferToWarehouseId('');
                      setTransferReceiverUserId('');
                    }
                  }}
                  label="Stock Item"
                  placeholder="Search IMEI last 5, SIM, serial, device"
                  emptyMessage="No transferable stock found"
                  required
                  inputStyle={inp}
                />
                {selectedTransferItem && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                    Current office location: <b>{selectedTransferItem.warehouse || 'Main'}</b>
                  </small>
                )}
              </div>
              <div>
                <label style={lbl}>To Office Location</label>
                <select
                  style={inp}
                  value={transferToWarehouseId}
                  onChange={e => {
                    setTransferToWarehouseId(e.target.value);
                    setTransferReceiverUserId('');
                    if (e.target.value) setTransferNewWarehouseName('');
                  }}
                  disabled={!selectedTransferItem}
                >
                  <option value="">{selectedTransferItem ? 'Select destination' : 'Select stock item first'}</option>
                  {transferDestinationWarehouseOptions.map(item => (
                    <option key={item.id} value={item.id}>
                      {warehouseOptionLabel(item)}
                    </option>
                  ))}
                </select>
                {selectedTransferItem && transferDestinationWarehouseOptions.length === 0 && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#dc2626', fontSize: '12px' }}>
                    No destination office location available for this workspace.
                  </small>
                )}
                {selectedTransferToWarehouse && selectedTransferToWarehouse.total_stock !== null && selectedTransferToWarehouse.total_stock !== undefined && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                    Destination stock: {selectedTransferToWarehouse.total_stock || 0}
                  </small>
                )}
              </div>
              {canModifyInventory && (
                <div>
                  <label style={lbl}>Or New Office Location</label>
                  <input
                    style={inp}
                    value={transferNewWarehouseName}
                    onChange={e => {
                      setTransferNewWarehouseName(e.target.value);
                      setTransferReceiverUserId('');
                      if (e.target.value) setTransferToWarehouseId('');
                    }}
                    placeholder="Create destination"
                  />
                </div>
              )}
              <div>
                <label style={lbl}>Receiver Employee{currentUser?.role === 'employee' ? ' *' : ''}</label>
                <select
                  style={inp}
                  value={transferReceiverUserId}
                  onChange={e => setTransferReceiverUserId(e.target.value)}
                  disabled={!transferToWarehouseId || receiverOptions.length === 0}
                >
                  <option value="">{receiverOptions.length > 0 ? 'Any assigned employee can receive' : 'Select destination first'}</option>
                  {receiverOptions.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name || `Employee #${item.id}`}{item.mobile ? ` (${item.mobile})` : ''}
                    </option>
                  ))}
                </select>
                {transferToWarehouseId && receiverOptions.length === 0 && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#dc2626', fontSize: '12px' }}>
                    Assign an employee to the destination office location.
                  </small>
                )}
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Transfer Note</label>
                <input style={inp} value={transferNote} onChange={e => setTransferNote(e.target.value)} placeholder="Reason / vehicle / courier details" />
              </div>
            </div>
            <button type="submit" disabled={loading} style={{
              marginTop: '16px', background: '#7c3aed', color: 'white',
              border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
            }}>
              {loading ? 'Transferring...' : 'Transfer Stock'}
            </button>
          </form>
          ) : (
          <form onSubmit={handleBulkTransferSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', alignItems: 'end', marginBottom: '18px' }}>
              <div>
                <label style={lbl}>From Office Location *</label>
                <select
                  style={inp}
                  value={bulkFromWarehouseId}
                  onChange={e => {
                    const nextFromId = e.target.value;
                    setBulkFromWarehouseId(nextFromId);
                    setBulkSelectedStockIds([]);
                    setBulkToWarehouseId('');
                    setBulkReceiverUserId('');
                  }}
                  required
                >
                  <option value="">Select source</option>
                  {bulkSourceWarehouseOptions.map(item => (
                    <option key={item.id} value={item.id}>{warehouseOptionLabel(item)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>To Office Location *</label>
                <select
                  style={inp}
                  value={bulkToWarehouseId}
                  onChange={e => {
                    setBulkToWarehouseId(e.target.value);
                    setBulkReceiverUserId('');
                  }}
                  disabled={!bulkFromWarehouseId}
                  required
                >
                  <option value="">{bulkFromWarehouseId ? 'Select destination' : 'Select source first'}</option>
                  {bulkDestinationWarehouseOptions.map(item => (
                    <option key={item.id} value={item.id}>
                      {warehouseOptionLabel(item)}
                    </option>
                  ))}
                </select>
                {bulkFromWarehouseId && bulkDestinationWarehouseOptions.length === 0 && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#dc2626', fontSize: '12px' }}>
                    No destination office location available for this workspace.
                  </small>
                )}
                {selectedBulkToWarehouse?.total_stock !== null && selectedBulkToWarehouse?.total_stock !== undefined && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                    Destination stock: {selectedBulkToWarehouse.total_stock || 0}
                  </small>
                )}
              </div>
              <div>
                <label style={lbl}>Receiver Employee{currentUser?.role === 'employee' ? ' *' : ''}</label>
                <select
                  style={inp}
                  value={bulkReceiverUserId}
                  onChange={e => setBulkReceiverUserId(e.target.value)}
                  disabled={!bulkToWarehouseId || bulkReceiverOptions.length === 0}
                >
                  <option value="">{bulkReceiverOptions.length > 0 ? 'Any assigned employee can receive' : 'Select destination first'}</option>
                  {bulkReceiverOptions.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name || `Employee #${item.id}`}{item.mobile ? ` (${item.mobile})` : ''}
                    </option>
                  ))}
                </select>
                {bulkToWarehouseId && bulkReceiverOptions.length === 0 && currentUser?.role === 'employee' && (
                  <small style={{ display: 'block', marginTop: '6px', color: '#dc2626', fontSize: '12px' }}>
                    Assign an employee to the destination office location.
                  </small>
                )}
              </div>
              <div>
                <label style={lbl}>Transfer Note</label>
                <input style={inp} value={bulkTransferNote} onChange={e => setBulkTransferNote(e.target.value)} placeholder="Reason / courier / branch note" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', alignItems: 'start' }}>
              <div style={{ border: '1px solid #ddd6fe', borderRadius: '10px', overflow: 'hidden', background: '#faf5ff' }}>
                <div style={{ padding: '12px', borderBottom: '1px solid #ddd6fe', background: '#f5f3ff' }}>
                  <strong style={{ color: '#5b21b6' }}>Available Stock</strong>
                  <small style={{ display: 'block', color: '#64748b', marginTop: '3px' }}>
                    {bulkFromWarehouseId ? `${bulkAvailableRows.length} ready item(s)` : 'Select source office location first'}
                  </small>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', marginTop: '10px' }}>
                    <input
                      style={inp}
                      value={bulkTransferSearch}
                      onChange={e => setBulkTransferSearch(e.target.value)}
                      placeholder="Search IMEI last 5, SIM, serial, device"
                      disabled={!bulkFromWarehouseId}
                    />
                    <button
                      type="button"
                      onClick={() => setBulkSelectedStockIds((current) => [...new Set([...current, ...bulkAvailableRows.map((item) => String(item.id))])])}
                      disabled={!bulkFromWarehouseId || bulkAvailableRows.length === 0}
                      style={{ border: 0, borderRadius: '8px', background: '#7c3aed', color: 'white', padding: '0 12px', cursor: 'pointer', fontWeight: 800 }}
                    >
                      Select All
                    </button>
                  </div>
                </div>
                <div style={{ maxHeight: '360px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', color: '#475569' }}>
                        <th style={{ width: '42px', padding: '10px' }}></th>
                        <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px' }}>Device</th>
                        <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px' }}>Office Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!bulkFromWarehouseId ? (
                        <tr><td colSpan="3" style={{ padding: '18px', textAlign: 'center', color: '#64748b' }}>Select source office location to show stock</td></tr>
                      ) : bulkAvailableRows.length === 0 ? (
                        <tr><td colSpan="3" style={{ padding: '18px', textAlign: 'center', color: '#64748b' }}>No available stock found</td></tr>
                      ) : bulkAvailableRows.map((item) => (
                        <tr key={item.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px' }}>
                            <input type="checkbox" checked={false} onChange={() => toggleBulkStockSelection(item.id)} />
                          </td>
                          <td style={{ padding: '10px', fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{bulkStockLabel(item)}</td>
                          <td style={{ padding: '10px', fontSize: '12px', color: '#64748b' }}>{item.warehouse || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ border: '1px solid #bbf7d0', borderRadius: '10px', overflow: 'hidden', background: '#f0fdf4' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', padding: '12px', borderBottom: '1px solid #bbf7d0', background: '#ecfdf5' }}>
                  <div>
                    <strong style={{ color: '#166534' }}>Selected For Transfer</strong>
                    <small style={{ display: 'block', color: '#64748b', marginTop: '3px' }}>{bulkSelectedRows.length} item(s) selected</small>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBulkSelectedStockIds([])}
                    disabled={bulkSelectedRows.length === 0}
                    style={{ border: '1px solid #fecaca', borderRadius: '8px', background: '#fff1f2', color: '#be123c', padding: '8px 10px', cursor: 'pointer', fontWeight: 800 }}
                  >
                    Remove All
                  </button>
                </div>
                <div style={{ maxHeight: '360px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', color: '#475569' }}>
                        <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px' }}>Device</th>
                        <th style={{ width: '86px', padding: '10px', fontSize: '12px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkSelectedRows.length === 0 ? (
                        <tr><td colSpan="2" style={{ padding: '18px', textAlign: 'center', color: '#64748b' }}>Tick stock from the available table</td></tr>
                      ) : bulkSelectedRows.map((item) => (
                        <tr key={item.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px', fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{bulkStockLabel(item)}</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => toggleBulkStockSelection(item.id)}
                              style={{ border: 0, borderRadius: '7px', background: '#fee2e2', color: '#b91c1c', padding: '7px 9px', cursor: 'pointer', fontWeight: 800 }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading || bulkSelectedRows.length === 0} style={{
              marginTop: '16px', background: '#7c3aed', color: 'white',
              border: 'none', padding: '11px 22px', borderRadius: '8px', cursor: loading || bulkSelectedRows.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 'bold'
            }}>
              {loading ? 'Transferring...' : `Transfer ${bulkSelectedRows.length} Device${bulkSelectedRows.length === 1 ? '' : 's'}`}
            </button>
          </form>
          )}
        </div>
      )}

      {inventoryPage === 'warehouse' && (
        <>
        {canModifyInventory && assignmentWarehouseId && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '16px', borderLeft: '5px solid #14b8a6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: '0 0 4px', color: '#0f766e' }}>Assign Office Location Employees</h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
                  Select stock access for {warehouses.find((item) => String(item.id) === String(assignmentWarehouseId))?.name || 'Selected office location'}.
                </p>
              </div>
              <button type="button" onClick={() => { setAssignmentWarehouseId(''); setAssignmentEmployeeIds([]); }} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                Cancel
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px', marginTop: '14px' }}>
              {employeesForAssignment.length === 0 ? (
                <div style={{ color: '#dc2626', fontSize: '13px', fontWeight: 'bold' }}>No active employee found in this admin workspace.</div>
              ) : employeesForAssignment.map((employee) => {
                const checked = assignmentEmployeeIds.includes(String(employee.id));
                return (
                  <label key={employee.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    border: `1px solid ${checked ? '#14b8a6' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    background: checked ? '#f0fdfa' : '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: '#0f172a'
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleAssignmentEmployee(employee.id)} />
                    <span>
                      {employee.name || `Employee #${employee.id}`}
                      <small style={{ display: 'block', color: '#64748b', fontWeight: 'normal', marginTop: '2px' }}>{employee.mobile || employee.email || '-'}</small>
                    </span>
                  </label>
                );
              })}
            </div>
            <button type="button" onClick={saveWarehouseAssignment} disabled={loading} style={{ marginTop: '14px', background: '#14b8a6', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              {loading ? 'Saving...' : 'Save Assignment'}
            </button>
          </div>
        )}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto', marginBottom: '24px' }}>
          <table style={{ width: '100%', minWidth: '980px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#14b8a6', color: 'white' }}>
                {['#', 'Office Location', ...(isSuperAdmin ? ['Admin'] : []), 'Address', 'Total', 'Available', 'Sold', 'Defective', 'Employees', 'Note', 'Actions'].map((head) => (
                  <th key={head} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {warehouses.length === 0 ? (
                <tr><td colSpan={isSuperAdmin ? 11 : 10} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No office location found</td></tr>
              ) : warehouses.map((item, i) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#0f766e' }}>{item.name}</td>
                  {isSuperAdmin && <td style={{ padding: '12px 16px', fontSize: '13px', color: '#334155', fontWeight: 'bold' }}>{item.admin_name || item.admin_id || '-'}</td>}
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.address || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>
                    {renderWarehouseStockDownload(item, 'all', item.total_stock, '#0f172a')}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#047857', fontWeight: 'bold' }}>
                    {renderWarehouseStockDownload(item, 'available', item.available_stock, '#047857')}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1d4ed8', fontWeight: 'bold' }}>
                    {renderWarehouseStockDownload(item, 'sold', item.sold_stock, '#1d4ed8')}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#dc2626', fontWeight: 'bold' }}>
                    {renderWarehouseStockDownload(item, 'defective', item.defective_stock, '#dc2626')}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', maxWidth: '220px' }}>{item.assigned_employee_names || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.note || '-'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {canModifyInventory ? (
                        <>
                          <button type="button" onClick={() => startAssignWarehouse(item)} style={{ background: '#e0f2fe', color: '#0369a1', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Assign</button>
                          <button type="button" onClick={() => startEditWarehouse(item)} style={{ background: '#ccfbf1', color: '#0f766e', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Edit</button>
                          {isSuperAdmin && <button type="button" onClick={() => deleteWarehouse(item)} disabled={Number(item.total_stock || 0) > 0} style={{ background: Number(item.total_stock || 0) > 0 ? '#f3f4f6' : '#fee2e2', color: Number(item.total_stock || 0) > 0 ? '#9ca3af' : '#dc2626', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: Number(item.total_stock || 0) > 0 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Delete</button>}
                        </>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 'bold' }}>View</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {inventoryPage === 'transfers' && (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: '12px', marginBottom: '14px' }}>
          <input
            style={inp}
            value={transferSearchTerm}
            onChange={e => setTransferSearchTerm(e.target.value)}
            placeholder="Search transfer by IMEI last 5, SIM, office location, employee..."
          />
          <select style={inp} value={transferStatusFilter} onChange={e => setTransferStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {eligibleFilteredTransfers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px' }}>
            <strong style={{ color: '#166534', fontSize: '13px' }}>{eligibleFilteredTransfers.length} pending receive item(s)</strong>
            <button
              type="button"
              onClick={() => setSelectedAcceptTransferIds(eligibleFilteredTransfers.map((item) => String(item.id)))}
              disabled={loading}
              style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', padding: '7px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
            >
              Select All Pending
            </button>
            <button
              type="button"
              onClick={() => setSelectedAcceptTransferIds([])}
              disabled={loading || selectedAcceptRows.length === 0}
              style={{ background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', padding: '7px 10px', borderRadius: '6px', cursor: selectedAcceptRows.length === 0 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={acceptSelectedTransfers}
              disabled={loading || selectedAcceptRows.length === 0}
              style={{ background: selectedAcceptRows.length === 0 ? '#f3f4f6' : '#16a34a', color: selectedAcceptRows.length === 0 ? '#9ca3af' : 'white', border: 'none', padding: '7px 12px', borderRadius: '6px', cursor: selectedAcceptRows.length === 0 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
            >
              Accept Selected ({selectedAcceptRows.length})
            </button>
            <button
              type="button"
              onClick={acceptAllVisiblePendingTransfers}
              disabled={loading}
              style={{ background: '#047857', color: 'white', border: 'none', padding: '7px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
            >
              Accept All Pending
            </button>
          </div>
        )}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto', marginBottom: '24px' }}>
          <table style={{ width: '100%', minWidth: '1480px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#475569', color: 'white' }}>
                {['Select', '#', 'Device', 'IMEI/SIM/Serial', 'From', 'To', 'Status', 'Receiver', 'Note', 'Transferred By', 'Date', 'Accepted By', 'Accepted Mobile', 'Accepted At', 'Actions'].map((head) => (
                  <th key={head} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredStockTransfers.length === 0 ? (
                <tr><td colSpan="15" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No stock transfer found</td></tr>
              ) : filteredStockTransfers.map((item, i) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                    <input
                      type="checkbox"
                      checked={selectedAcceptTransferIds.includes(String(item.id))}
                      onChange={() => toggleAcceptTransferSelection(item.id)}
                      disabled={!canAcceptTransfer(item)}
                    />
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{item.device_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.imei || item.sim_no || item.serial_no || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.from_warehouse || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f766e', fontWeight: 'bold' }}>{item.to_warehouse || '-'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: item.status === 'pending' ? '#fef3c7' : item.status === 'accepted' ? '#d1fae5' : item.status === 'rejected' ? '#fee2e2' : '#e5e7eb',
                      color: item.status === 'pending' ? '#92400e' : item.status === 'accepted' ? '#065f46' : item.status === 'rejected' ? '#dc2626' : '#374151'
                    }}>
                      {item.status || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.receiver_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.note || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.transferred_by_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.created_at ? formatDate(item.created_at) : '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: item.accepted_by_name ? 'bold' : 'normal', color: item.accepted_by_name ? '#166534' : '#64748b' }}>{item.accepted_by_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.accepted_by_mobile || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.accepted_at ? formatDate(item.accepted_at) : '-'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {canAcceptTransfer(item) && (
                        <button type="button" onClick={() => decideTransfer(item, 'accept')} style={{ background: '#dcfce7', color: '#166534', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Accept</button>
                      )}
                      {canRejectTransfer(item) && (
                        <button type="button" onClick={() => decideTransfer(item, 'reject')} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Reject</button>
                      )}
                      {canCancelTransfer(item) && (
                        <button type="button" onClick={() => decideTransfer(item, 'cancel')} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Cancel</button>
                      )}
                      {!canAcceptTransfer(item) && !canRejectTransfer(item) && !canCancelTransfer(item) && (
                        <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 'bold' }}>-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {showTypeForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#0ea5e9', marginTop: 0 }}>Add Product Type</h3>
          <form onSubmit={handleAddProductType}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'end' }}>
              <div>
                <label style={lbl}>Product Type Name *</label>
                <input style={inp} value={productTypeName} onChange={e => setProductTypeName(e.target.value)} placeholder="Example: GPS, Camera, Sensor, SIM" required />
              </div>
              <button type="submit" disabled={loading} style={{
                background: '#0ea5e9', color: 'white',
                border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
              }}>
                {loading ? 'Saving...' : 'Save Type'}
              </button>
            </div>
            {productTypes.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                {productTypes.map(t => (
                  <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: t.admin_id === 0 ? '#e0f2fe' : '#dcfce7', color: t.admin_id === 0 ? '#0369a1' : '#166534', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold' }}>
                    <button type="button" onClick={() => { setPType(t.name); setStockDeviceType(t.name); setNewDeviceNameType(t.name); }} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 'bold' }}>{t.name}</button>
                    {canManageMaster(t) && <button type="button" onClick={() => updateProductType(t)} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '11px' }}>Edit</button>}
                    {canManageMaster(t) && <button type="button" onClick={() => deleteProductType(t)} style={{ border: 0, background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '11px' }}>Delete</button>}
                  </span>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {showOemForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#0f766e', marginTop: 0 }}>Add Device OEM Name</h3>
          <form onSubmit={handleAddDeviceOemName}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'end' }}>
              <div>
                <label style={lbl}>Device OEM Name *</label>
                <input style={inp} value={newDeviceOemName} onChange={e => setNewDeviceOemName(e.target.value)} placeholder="Example: Concox, Teltonika, Queclink" required />
              </div>
              <button type="submit" disabled={loading} style={{
                background: '#0f766e', color: 'white',
                border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
              }}>
                {loading ? 'Saving...' : 'Save OEM'}
              </button>
            </div>
            {deviceOems.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                {deviceOems.map(oem => (
                  <span key={oem.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ccfbf1', color: '#115e59', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold' }}>
                    <button type="button" onClick={() => { setDeviceOemName(oem.name); setNewDeviceNameOem(oem.name); }} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 'bold' }}>{oem.name}</button>
                    {canManageMaster(oem) && <button type="button" onClick={() => updateDeviceOem(oem)} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '11px' }}>Edit</button>}
                    {canManageMaster(oem) && <button type="button" onClick={() => deleteDeviceOem(oem)} style={{ border: 0, background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '11px' }}>Delete</button>}
                  </span>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {showDeviceNameForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#8b5cf6', marginTop: 0 }}>Add Device Name</h3>
          <form onSubmit={handleAddDeviceName}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
              <div>
                <label style={lbl}>Device Name *</label>
                <input style={inp} value={newDeviceName} onChange={e => setNewDeviceName(e.target.value)} placeholder="Example: GT06N Tracker" required />
              </div>
              <div>
                <label style={lbl}>Device OEM Name</label>
                <input style={inp} list="device-oem-options" value={newDeviceNameOem} onChange={e => setNewDeviceNameOem(e.target.value)} placeholder="Select or type OEM" />
              </div>
              <div>
                <label style={lbl}>Device Type</label>
                <select style={inp} value={newDeviceNameType} onChange={e => setNewDeviceNameType(e.target.value)}>
                  {productTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              <button type="submit" disabled={loading} style={{
                background: '#8b5cf6', color: 'white',
                border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
              }}>
                {loading ? 'Saving...' : 'Save Device'}
              </button>
            </div>
            <datalist id="device-oem-options">
              {deviceOems.map(oem => <option key={oem.id} value={oem.name} />)}
            </datalist>
            {deviceNames.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                {deviceNames.map(device => (
                  <span key={device.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ede9fe', color: '#5b21b6', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold' }}>
                    <button type="button" onClick={() => fillStockFromDeviceName(device.name)} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 'bold' }}>
                      {device.name}{device.device_oem_name ? ` | ${device.device_oem_name}` : ''}
                    </button>
                    {canManageMaster(device) && <button type="button" onClick={() => updateDeviceMaster(device)} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '11px' }}>Edit</button>}
                    {canManageMaster(device) && <button type="button" onClick={() => deleteDeviceMaster(device)} style={{ border: 0, background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '11px' }}>Delete</button>}
                  </span>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {showProductForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#8b5cf6', marginTop: 0 }}>Add New Product</h3>
          <form onSubmit={handleAddProduct}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={lbl}>Product Name *</label>
                <input style={inp} value={pName} onChange={e => setPName(e.target.value)} placeholder="Product name" required />
              </div>
              <div>
                <label style={lbl}>Type *</label>
                <select style={inp} value={pType} onChange={e => setPType(e.target.value)} required>
                  <option value="">Select product type</option>
                  {productTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
                <small style={{ color: '#6b7280', display: 'block', marginTop: '6px' }}>
                  Need a new type? Add it first with + Product Type.
                </small>
              </div>
              <div>
                <label style={lbl}>Model</label>
                <input style={inp} value={pModel} onChange={e => setPModel(e.target.value)} placeholder="Model number" />
              </div>
            </div>
            <button type="submit" disabled={loading} style={{
              marginTop: '16px', background: '#8b5cf6', color: 'white',
              border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
            }}>
              {loading ? 'Saving...' : 'Save Product'}
            </button>
            {products.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                {products.map(product => (
                  <span key={product.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#eef2ff', color: '#3730a3', padding: '6px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold' }}>
                    <button type="button" onClick={() => { setProductId(product.id); setPName(product.name); setPType(product.type); setPModel(product.model || ''); }} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 'bold' }}>
                      {product.name} ({product.type})
                    </button>
                    {canManageMaster(product) && <button type="button" onClick={() => updateProductMaster(product)} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '11px' }}>Edit</button>}
                    {canManageMaster(product) && <button type="button" onClick={() => deleteProductMaster(product)} style={{ border: 0, background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '11px' }}>Delete</button>}
                  </span>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {showStockForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#1e3a5f', marginTop: 0 }}>{editStockId ? 'Edit Stock' : 'Add Stock'}</h3>
          <form onSubmit={handleAddStock}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={lbl}>Device Name</label>
                <input
                  style={inp}
                  value={deviceName}
                  onChange={e => fillStockFromDeviceName(e.target.value)}
                  placeholder="GT06N Tracker / FMB920 Tracker"
                  autoComplete="off"
                />
                {deviceNames.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {deviceNames.slice(0, 6).map(device => (
                      <button type="button" key={device.id} onClick={() => fillStockFromDeviceName(device.name)} style={{
                        border: '1px solid #ddd6fe', background: '#f5f3ff', color: '#5b21b6',
                        borderRadius: '999px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold'
                      }}>
                        {device.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={lbl}>Device Oem Name</label>
                <input
                  style={inp}
                  value={deviceOemName}
                  onChange={e => setDeviceOemName(e.target.value)}
                  placeholder="Concox / Teltonika / Queclink"
                  autoComplete="off"
                />
                {deviceOems.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {deviceOems.slice(0, 6).map(oem => (
                      <button type="button" key={oem.id} onClick={() => setDeviceOemName(oem.name)} style={{
                        border: '1px solid #99f6e4', background: '#f0fdfa', color: '#115e59',
                        borderRadius: '999px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold'
                      }}>
                        {oem.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={lbl}>Device Type</label>
                <input
                  style={inp}
                  value={stockDeviceType}
                  onChange={e => setStockDeviceType(e.target.value)}
                  placeholder="GPS / AIS140 / Camera"
                  autoComplete="off"
                />
              </div>
              <div>
                <label style={lbl}>Existing Product Optional</label>
                <select style={inp} value={productId} onChange={e => setProductId(e.target.value)}>
                  <option value="">Auto create from Device Name</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>IMEI Number</label>
                <input style={inp} value={imei} onChange={e => setImei(e.target.value)} placeholder="15 digit IMEI" />
              </div>
              <div>
                <label style={lbl}>SIM Number 1</label>
                <input style={inp} value={simNo} onChange={e => setSimNo(e.target.value)} placeholder="SIM number" />
              </div>
              <div>
                <label style={lbl}>SIM Number 2</label>
                <input style={inp} value={simNo2} onChange={e => setSimNo2(e.target.value)} placeholder="Second SIM number" />
              </div>
              <div>
                <label style={lbl}>Serial No</label>
                <input style={inp} value={serialNo} onChange={e => setSerialNo(e.target.value)} placeholder="Serial number" />
              </div>
              <div>
                <label style={lbl}>Office Location</label>
                <select style={inp} value={warehouse} onChange={e => setWarehouse(e.target.value)}>
                  {warehouseOptions.length === 0 && <option value="Main">Main</option>}
                  {warehouseOptions.map(item => <option key={`${item.id || item.name}`} value={item.name}>{item.name}</option>)}
                </select>
                <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '12px' }}>
                  Use + Office Location in the Inventory module to create a new office location.
                </small>
              </div>
              {canViewPurchaseDetails && (
                <>
                  <div>
                    <label style={lbl}>Purchase Price</label>
                    <input type="number" style={inp} value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} placeholder="Price" />
                  </div>
                  <div>
                    <label style={lbl}>Purchase Date</label>
                    <input type="date" style={inp} value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} />
                  </div>
                </>
              )}
              <div>
                <label style={lbl}>Status</label>
                <select style={inp} value={stockStatus} onChange={e => setStockStatus(e.target.value)}>
                  <option value="available">Available</option>
                  <option value="sold">Sold</option>
                  <option value="defective">Defective</option>
                </select>
              </div>
              {canViewPurchaseDetails && (
                <>
                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '4px' }}>
                    <h4 style={{ margin: '0 0 4px', color: '#1e3a5f' }}>Purchase Details</h4>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>Vendor, invoice, and payment details will be linked with the inventory purchase ledger.</p>
                  </div>
                  <div>
                    <label style={lbl}>Vendor Name</label>
                    <input style={inp} value={vendorName} onChange={e => setVendorName(e.target.value)} placeholder="Vendor name" autoComplete="off" />
                    {vendors.length > 0 && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                        {vendors.slice(0, 6).map(vendor => (
                          <button type="button" key={vendor.id} onClick={() => fillVendor(vendor)} style={{
                            border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8',
                            borderRadius: '999px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold'
                          }}>
                            {vendor.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={lbl}>Vendor Mobile</label>
                    <input style={inp} value={vendorMobile} onChange={e => setVendorMobile(e.target.value)} placeholder="Vendor mobile" />
                  </div>
                  <div>
                    <label style={lbl}>Invoice Number</label>
                    <input style={inp} value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} placeholder="Invoice / bill number" />
                  </div>
                  <div>
                    <label style={lbl}>Payment Mode</label>
                    <select style={inp} value={paymentMode} onChange={e => setPaymentMode(e.target.value)}>
                      <option value="credit">Credit</option>
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                      <option value="upi">UPI</option>
                      <option value="gpay">GPay</option>
                      <option value="paytm">Paytm</option>
                      <option value="phonepe">PhonePe</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Paid Amount</label>
                    <input type="number" style={inp} value={paidAmount} onChange={e => setPaidAmount(e.target.value)} placeholder="Paid amount" />
                  </div>
                  <div>
                    <label style={lbl}>Purchase Note</label>
                    <input style={inp} value={purchaseNote} onChange={e => setPurchaseNote(e.target.value)} placeholder="Purchase note" />
                  </div>
                </>
              )}
            </div>
            <button type="submit" disabled={loading} style={{
              marginTop: '16px', background: '#10b981', color: 'white',
              border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
            }}>
              {loading ? 'Saving...' : editStockId ? 'Update Stock' : 'Save Stock'}
            </button>
            {editStockId && (
              <button type="button" onClick={() => { resetStockForm(); setShowStockForm(false); }} style={{
                marginTop: '16px', marginLeft: '10px', background: '#f3f4f6', color: '#374151',
                border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
              }}>
                Cancel Edit
              </button>
            )}
          </form>
        </div>
      )}

      {inventoryPage === 'lowStock' && (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '720px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f59e0b', color: 'white' }}>
                {['#', 'Product Name', 'Product Type', 'Available Quantity', 'Alert'].map((head) => (
                  <th key={head} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleLowStock.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                    No low stock products found
                  </td>
                </tr>
              ) : visibleLowStock.map((item, i) => (
                <tr key={`${item.name}-${item.type}-${i}`} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#fffbeb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{item.name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.type || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.available_count || 0}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', background: '#fef3c7', color: '#92400e' }}>
                      Below 5
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inventoryTablePages.includes(inventoryPage) && (
      <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: '12px', marginBottom: '14px' }}>
        <input
          style={inp}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder={canViewPurchaseDetails ? 'Search by device, IMEI, SIM, vendor, invoice, office location...' : 'Search by device, IMEI, SIM, office location...'}
        />
        <select style={inp} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="sold">Sold</option>
          <option value="defective">Defective</option>
        </select>
      </div>

      {viewMode === 'returns' ? (
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '1520px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1e3a5f', color: 'white' }}>
              {['#', 'Product', 'Device Type', 'IMEI', 'Serial No', 'Received From', 'Mobile', 'Vehicle', 'Send To OEM', 'Condition', 'Reason', 'Returned By', 'Date'].map((head) => (
                <th key={head} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredReturns.length === 0 ? (
              <tr>
                <td colSpan="13" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                  No inventory returns found
                </td>
              </tr>
            ) : filteredReturns.map((item, i) => (
              <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{item.product_name || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.product_type || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.imei || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.serial_no || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.received_from_customer_name || item.customer_name || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.received_from_customer_mobile || item.customer_mobile || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.received_from_vehicle_no || item.vehicle_no || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{item.send_to_oem_display_name || item.send_to_oem_name || '-'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold',
                    background: item.condition_status === 'good' ? '#d1fae5' : '#fee2e2',
                    color: item.condition_status === 'good' ? '#065f46' : '#dc2626'
                  }}>
                    {item.condition_status || '-'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.reason || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.returned_by_name || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{item.created_at ? formatDate(item.created_at) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      ) : (
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: canViewPurchaseDetails ? '2040px' : '1400px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1e3a5f', color: 'white' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>#</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Device Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Device Oem Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Device Type</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>IMEI Number</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>SIM Number 1</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>SIM Number 2</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Serial No</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Office Location</th>
              {canViewPurchaseDetails && (
                <>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Price</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Purchase Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Vendor</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Invoice</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Payment</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Paid</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Note</th>
                </>
              )}
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Sale Price</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Status</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleStock.length === 0 ? (
              <tr>
                <td colSpan={canViewPurchaseDetails ? 19 : 12} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                  No stock found
                </td>
              </tr>
            ) : visibleStock.map((s, i) => (
              <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{s.device_name || s.name}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.device_oem_name || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.device_type || s.type}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.imei || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.sim_no || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.sim_no_2 || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.serial_no || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <span>{s.warehouse || 'Main'}</span>
                    {isTransferPending(s) && (
                      <span style={{ alignSelf: 'flex-start', padding: '3px 8px', borderRadius: '999px', background: '#fef3c7', color: '#92400e', fontSize: '11px', fontWeight: 'bold' }}>
                        Pending Transfer
                      </span>
                    )}
                    {isTransferPending(s) && (
                      <small style={{ color: '#64748b', fontSize: '11px' }}>
                        To: {s.pending_to_warehouse || '-'}{s.pending_receiver_name ? ` | ${s.pending_receiver_name}` : ''}
                      </small>
                    )}
                  </div>
                </td>
                {canViewPurchaseDetails && (
                  <>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>Rs. {s.purchase_price || 0}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.purchase_date ? formatDate(s.purchase_date) : '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.vendor_name || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.invoice_no || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.payment_mode || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>Rs. {s.paid_amount || 0}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.purchase_note || '-'}</td>
                  </>
                )}
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: s.status === 'sold' ? 'bold' : 'normal', color: s.status === 'sold' ? '#0f172a' : '#64748b' }}>
                  {s.status === 'sold' && s.sale_price !== null && s.sale_price !== undefined ? `Rs. ${s.sale_price}` : '-'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold',
                    background: s.status === 'available' ? '#d1fae5' : s.status === 'sold' ? '#dbeafe' : '#fee2e2',
                    color: s.status === 'available' ? '#065f46' : s.status === 'sold' ? '#1d4ed8' : '#dc2626'
                  }}>
                    {s.status}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {canTransferStock && (
                      <button
                        type="button"
                        onClick={() => startTransferStock(s)}
                        disabled={s.status === 'sold' || isTransferPending(s)}
                        style={{
                          background: s.status === 'sold' || isTransferPending(s) ? '#f3f4f6' : '#ede9fe',
                          color: s.status === 'sold' || isTransferPending(s) ? '#9ca3af' : '#6d28d9',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '6px',
                          cursor: s.status === 'sold' || isTransferPending(s) ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        Transfer
                      </button>
                    )}
                    {canModifyInventory ? (
                      <>
                        <button type="button" onClick={() => startEditStock(s)} disabled={isTransferPending(s)} style={{ background: isTransferPending(s) ? '#f3f4f6' : '#dbeafe', color: isTransferPending(s) ? '#9ca3af' : '#1d4ed8', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: isTransferPending(s) ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Edit</button>
                        {isSuperAdmin && <button type="button" onClick={() => cleanStockDetails(s)} style={{ background: '#fef9c3', color: '#854d0e', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Clean</button>}
                        {isSuperAdmin && <button type="button" onClick={() => deleteStock(s)} disabled={isTransferPending(s)} style={{ background: isTransferPending(s) ? '#f3f4f6' : '#fee2e2', color: isTransferPending(s) ? '#9ca3af' : '#dc2626', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: isTransferPending(s) ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Delete</button>}
                      </>
                    ) : (
                      !canTransferStock && <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 'bold' }}>View</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      </>
      )}
      </>
      )}
      </div>
    </div>
  );
}
